import { matchingFishingTitles, expressionUsesTitles } from "./search-expression/title.js";
import { QUERY_FILTER_ICONS } from "../js/query/presentation.js";
import { findZoneMatches, parseZoneRgbSearch } from "./map-zone-catalog.js";
import {
  QUERY_MATCH_RANK_EXACT,
  QUERY_MATCH_RANK_NONE,
  QUERY_MATCH_RANK_PREFIX,
  compactQueryMatchText,
  createPreparedQueryMatchCacheTransaction,
  matchQueryValues,
  normalizeQueryMatchText,
  prepareQueryMatchState,
} from "../js/query/match.js";
import {
  queryInputSuggestionExactMatchesValue,
  rankQueryInputSuggestion,
  selectBoundedQueryInputCandidates,
} from "./map-query-suggestion-ranking.js";
import {
  createQueryCatalogIndexCache,
  queryCatalogCandidates,
  queryCatalogExactCandidates,
  queryCatalogOneCharacterCandidates,
  queryCatalogPrefixCandidates,
  queryCatalogRecordMatch,
} from "../js/query/catalog-index.js";
import { projectSearchCatalog } from "./map-search-catalog-projection.js";
import {
  searchExpressionTokenLower,
  searchExpressionTokenText,
  tokenizeSearchExpressionDsl,
} from "../js/query/lex.js";
import {
  prepareSearchExpressionCatalogIndexes,
  resolveSearchExpressionItemNameGroup,
  resolveSearchExpressionTradeNpcKey,
} from "./search-expression/resolve.js";
import {
  specialItemReferencesForCatalogItems,
  GOD_OF_FISHING_TITLE_ID,
} from "./map-special-item-references.js";
import {
  FISH_FILTER_TERM_ORDER,
  normalizeFishFilterTerm,
  normalizeFishFilterTerms,
  normalizeLayerFilterId,
  normalizePatchBound,
  normalizePatchId,
  normalizeRawSearchExpressionTree,
  normalizeSearchGradeTerm,
  normalizeSearchStatusTerm,
  normalizeSourceKind,
  searchTermKey,
  normalizeSourceKinds,
  parseSearchExpressionDslResult,
  resolveSearchExpression,
  resolveSelectedSearchTerms,
  serializeSearchExpressionDsl,
  tradeNpcKeyFromSearchTerm,
} from "./map-search-contract.js";
import { currentLocale, mapText, siteText } from "./map-i18n.js";

export { normalizeFishFilterTerm, normalizeFishFilterTerms } from "./map-search-contract.js";
export { specialItemReferencesForCatalogItems } from "./map-special-item-references.js";
const FISH_GRADE_FILTER_TERMS = new Set(["red", "yellow", "blue", "green", "white"]);

const ITEM_SOURCE_KIND_RESULT_ORDER = new Map([
  ["rod", 0],
  ["hotspot", 1],
  ["harpoon", 2],
  ["zone", 3],
  ["ice_fishing", 4],
]);

const QUERY_INPUT_SUGGESTION_LIMIT = 12;
const QUERY_INPUT_DEFAULT_FILTER_TERMS = Object.freeze([
  "favourite",
  "missing",
  "missing-titles",
  "red",
  "yellow",
  "blue",
]);
const QUERY_INPUT_BOOLEAN_OPERATOR_SUGGESTIONS = Object.freeze([
  Object.freeze({
    kind: "operator",
    label: "AND",
    description: "match all",
    insertText: "AND",
    aliases: Object.freeze(["and", "all"]),
    score: 311,
  }),
  Object.freeze({
    kind: "operator",
    label: "OR",
    description: "match any",
    insertText: "OR",
    aliases: Object.freeze(["or", "any"]),
    score: 310,
  }),
  Object.freeze({
    kind: "operator",
    label: "NOT",
    description: "exclude",
    insertText: "NOT",
    aliases: Object.freeze(["not", "exclude"]),
    score: 309,
  }),
]);
const QUERY_INPUT_SPACED_VALUE_PREFIXES = new Set([
  "title",
  "fish",
  "item",
  "npc",
  "semantic",
  "patch",
  "from",
  "to",
  "zone",
  "region",
  "regions",
  "regiongroup",
  "regiongroups",
  "region_group",
  "region_groups",
]);
const QUERY_INPUT_SOURCE_KIND_SUGGESTIONS = Object.freeze([
  {
    sourceKind: "rod",
    aliases: [],
    label: siteText("domain.method.rod"),
    description: "item source",
  },
  {
    sourceKind: "hotspot",
    aliases: [],
    label: siteText("domain.source.hotspot"),
    description: "item source",
  },
  {
    sourceKind: "harpoon",
    aliases: [],
    label: siteText("domain.method.harpoon"),
    description: "item source",
  },
  {
    sourceKind: "zone",
    aliases: [],
    label: siteText("domain.source.zone"),
    description: "rod or harpoon item source",
  },
  {
    sourceKind: "ice_fishing",
    aliases: ["ice-fishing"],
    label: siteText("domain.source.ice_fishing"),
    description: "item source",
  },
]);

const QUERY_INPUT_LAYER_FILTER_SCHEMAS = Object.freeze({
  zones: {
    layerId: "zones",
    label: "Fishing zones",
    aliases: ["zones", "zone", "zone mask", "fishing zones", "loot"],
    subjects: [
      { prefix: "fish", label: "Fish in zone loot", insertText: "fish:", score: 260 },
      { prefix: "item", label: "Item in zone loot", insertText: "item:", score: 255 },
    ],
    groups: [
      { id: "prize", label: "Prize" },
      { id: "high_quality", label: "High Quality" },
      { id: "rare", label: "Rare" },
      { id: "trash", label: "Trash" },
      { id: "harpoon", label: "Harpoon" },
    ],
    snippets: [
      {
        kind: "layer-filter-snippet",
        label: "High-quality group >= 50%",
        description: "zones where the high-quality group has at least 50% base rate",
        insertText: "group:high_quality >= 50%",
        aliases: ["high quality group", "blue group", "group rate"],
        score: 248,
      },
      {
        kind: "layer-filter-snippet",
        label: "Fish in Prize at 30%",
        description: "fish:<id> IN prize = 30%",
        insertText: "fish: IN prize = 30%",
        aliases: ["fish prize 30", "30 10", "prize rate"],
        score: 244,
      },
      {
        kind: "layer-filter-snippet",
        label: "Item absent from Prize",
        description: "item:<id> NOT IN prize",
        insertText: "item: NOT IN prize",
        aliases: ["item absent prize", "not in prize"],
        score: 242,
      },
    ],
    rateSuggestions: ["= 30%", "= 10%", "<= 50%", ">= 50%"],
  },
  hotspots: {
    layerId: "hotspots",
    label: "Hotspots",
    aliases: ["hotspots", "hotspot"],
    fields: [
      { id: "model_action", label: "Model action", type: "number", example: "model_action = 3" },
    ],
    snippets: [
      {
        kind: "layer-filter-snippet",
        label: "Model action = 3",
        description: "hotspots using model action 3",
        insertText: "model_action = 3",
        aliases: ["model action", "action", "3d model"],
        score: 248,
      },
    ],
  },
  bookmarks: {
    layerId: "bookmarks",
    label: "Bookmarks",
    aliases: ["bookmarks", "bookmark"],
    relations: [
      { id: "inside", label: "Inside", targetPrefix: "zone", insertText: "inside zone:" },
    ],
    snippets: [
      {
        kind: "layer-filter-snippet",
        label: "Inside zone",
        description: "bookmarks placed inside a fishing zone",
        insertText: "inside zone:",
        aliases: ["inside zone", "zone"],
        score: 248,
      },
    ],
  },
});

const EMPTY_QUERY_INPUT_ARRAY = Object.freeze([]);
const NORMALIZED_PATCH_CATALOG_CACHE = new WeakMap();
const NORMALIZED_ITEM_CATALOG_CACHE = new WeakMap();
const FISH_IDENTITY_LOOKUP_CACHE = new WeakMap();
const ITEM_IDENTITY_LOOKUP_CACHE = new WeakMap();
const TRADE_NPC_IDENTITY_LOOKUP_CACHE = new WeakMap();
const SEMANTIC_TERM_LOOKUP_CACHE = new WeakMap();
const FISH_SEARCH_VALUES_CACHE = new WeakMap();
const ITEM_SEARCH_VALUES_CACHE = new WeakMap();
const SEMANTIC_SEARCH_VALUES_CACHE = new WeakMap();
const TRADE_NPC_SEARCH_VALUES_CACHE = new WeakMap();
const FISH_PREPARED_SEARCH_VALUES_CACHE = new WeakMap();
const ITEM_PREPARED_SEARCH_VALUES_CACHE = new WeakMap();
const SEMANTIC_PREPARED_SEARCH_VALUES_CACHE = new WeakMap();
const SEARCH_MATCH_SUGGESTION_KEY_CACHE = new WeakMap();
const FISH_CATALOG_INDEX = createValueCatalogIndex(fishSearchValues, {
  preparedValues: (fish) => FISH_PREPARED_SEARCH_VALUES_CACHE.get(fish),
  oneCharacterValues: fishSearchValues,
  oneCharacterLabel: (fish) => fish?.name,
  oneCharacterIdentityKey: (fish) => fish?.itemId ?? fish?.fishId,
});
const ITEM_SEARCH_CATALOG_INDEX = createValueCatalogIndex(itemSearchValues, {
  preparedValues: (item) => ITEM_PREPARED_SEARCH_VALUES_CACHE.get(item),
  oneCharacterLabel: (item) => item?.name,
  oneCharacterIdentityKey: (item) => item?.itemId,
});
const PATCH_CATALOG_INDEX = createValueCatalogIndex(patchCatalogIndexValues);
const SEMANTIC_CATALOG_INDEX = createValueCatalogIndex(semanticSearchValues, {
  preparedValues: (term) => SEMANTIC_PREPARED_SEARCH_VALUES_CACHE.get(term),
  oneCharacterValues: semanticSearchValues,
  oneCharacterLabel: (term) => term?.name || term?.label,
  oneCharacterIdentityKey: (term) => semanticTermLookupKey(term?.layerId, term?.fieldId),
});
const TRADE_NPC_CATALOG_INDEX = createValueCatalogIndex(tradeNpcSearchValues, {
  oneCharacterValues: tradeNpcSearchValues,
  oneCharacterLabel: (npc) => npc?.npcName,
  oneCharacterIdentityKey: (npc) => npc?.npcKey,
});
const SPECIAL_ITEM_CATALOG_INDEX = createValueCatalogIndex(specialItemReferenceSuggestionLabels);
const ZONE_CATALOG_INDEX = createValueCatalogIndex(zoneCatalogIndexValues, {
  oneCharacterValues: zoneNameSearchValues,
  oneCharacterLabel: (zone) => zone?.name,
  oneCharacterIdentityKey: (zone) => zone?.zoneRgb,
});
let plainCatalogPhraseLookupCache = null;
let fishFilterMetadataCache = null;

function createValueCatalogIndex(valuesForEntry, options = {}) {
  return createQueryCatalogIndexCache((value, index) => {
    const values = valuesForEntry(value);
    const preparedValues = options.preparedValues?.(value);
    const preparesOneCharacterRanking =
      typeof options.oneCharacterValues === "function" ||
      typeof options.oneCharacterLabel === "function" ||
      typeof options.oneCharacterIdentityKey === "function";
    return {
      key: `entry:${index}`,
      values,
      ...(Array.isArray(preparedValues) ? { preparedValues } : {}),
      value,
      ...(preparesOneCharacterRanking
        ? {
            oneCharacter: {
              values: options.oneCharacterValues?.(value) ?? values,
              label: options.oneCharacterLabel?.(value) ?? value?.name ?? value?.label ?? "",
              identityKey: options.oneCharacterIdentityKey?.(value) ?? "",
            },
          }
        : {}),
    };
  }, { mode: "scan" });
}

function fishFilterMetadata() {
  const locale = currentLocale();
  if (locale && fishFilterMetadataCache?.locale === locale) {
    return fishFilterMetadataCache.value;
  }
  const value = {
    favourite: {
      label: mapText("search.filter.favourite.label"),
      description: mapText("search.filter.favourite.description"),
      searchText: "favourite favourites favorite favorites heart liked",
      ...QUERY_FILTER_ICONS.favourite,
    },
    "missing-titles": {
      label: siteText("fishydex.query.term.missing-titles"),
      description: siteText("map.search.filter.missing_titles.description"),
      searchText: "missing-titles titles unearned unobtained",
      ...QUERY_FILTER_ICONS["missing-titles"],
    },
    missing: {
      label: mapText("search.filter.missing.label"),
      description: mapText("search.filter.missing.description"),
      searchText: "missing uncaught not caught not yet caught",
      ...QUERY_FILTER_ICONS.missing,
    },
    red: {
      label: siteText("domain.grade.red"),
      description: mapText("search.filter.red.description"),
      searchText: "red prize red-grade fish loot item grade",
      ...QUERY_FILTER_ICONS.red,
    },
    yellow: {
      label: siteText("domain.grade.yellow"),
      description: mapText("search.filter.yellow.description"),
      searchText: "yellow rare yellow-grade fish loot item grade",
      ...QUERY_FILTER_ICONS.yellow,
    },
    blue: {
      label: siteText("domain.grade.blue"),
      description: mapText("search.filter.blue.description"),
      searchText: "blue highquality high-quality high quality blue-grade fish loot item grade",
      ...QUERY_FILTER_ICONS.blue,
    },
    green: {
      label: siteText("domain.grade.green"),
      description: mapText("search.filter.green.description"),
      searchText: "green general green-grade fish loot item grade",
      ...QUERY_FILTER_ICONS.green,
    },
    white: {
      label: siteText("domain.grade.white"),
      description: mapText("search.filter.white.description"),
      searchText: "white trash white-grade fish loot item grade",
      ...QUERY_FILTER_ICONS.white,
    },
  };
  if (!locale) {
    return value;
  }
  Object.values(value).forEach(Object.freeze);
  fishFilterMetadataCache = {
    locale,
    value: Object.freeze(value),
  };
  return fishFilterMetadataCache.value;
}

function fishFilterSearchValues(term, metadata) {
  return [
    term,
    metadata?.label,
  ].map((value) => String(value || "").trim()).filter(Boolean);
}

function patchBoundPromptMatches() {
  return [
    {
      bound: "from",
      label: mapText("search.patch.prompt.after"),
      description: mapText("search.patch.prompt.description"),
      searchText: "after from since patch date newer later",
    },
    {
      bound: "to",
      label: mapText("search.patch.prompt.before"),
      description: mapText("search.patch.prompt.description"),
      searchText: "before to until through patch date older earlier",
    },
  ];
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeStringList(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = String(value ?? "").trim();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }
  return next;
}

function normalizeQueryIssueSpan(span) {
  const source = isPlainObject(span) ? span : {};
  const start = Number.parseInt(source.start, 10);
  const end = Number.parseInt(source.end, 10);
  return {
    start: Number.isInteger(start) && start >= 0 ? start : 0,
    end: Number.isInteger(end) && end >= 0 ? end : 0,
  };
}

function normalizeSearchExpressionIssues(issues) {
  if (!Array.isArray(issues)) {
    return [];
  }
  return issues.flatMap((issue) => {
    if (!isPlainObject(issue)) {
      return [];
    }
    const stage = String(issue.stage || "").trim();
    const severity = issue.severity === "warning" ? "warning" : "error";
    const code = String(issue.code || "").trim();
    if (!stage || !code) {
      return [];
    }
    return [{
      stage,
      severity,
      code,
      span: normalizeQueryIssueSpan(issue.span),
      raw: String(issue.raw ?? ""),
      message: String(issue.message || ""),
    }];
  });
}

function normalizeIntegerList(values) {
  if (!Array.isArray(values)) {
    return [];
  }
  const next = [];
  const seen = new Set();
  for (const value of values) {
    const normalized = Number.parseInt(value, 10);
    if (!Number.isInteger(normalized) || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    next.push(normalized);
  }
  return next;
}

function normalizeSemanticFieldIdsByLayer(values) {
  if (!isPlainObject(values)) {
    return {};
  }
  const out = {};
  for (const [layerIdRaw, fieldIdsRaw] of Object.entries(values)) {
    const layerId = String(layerIdRaw ?? "").trim();
    if (!layerId || !Array.isArray(fieldIdsRaw)) {
      continue;
    }
    const fieldIds = normalizeIntegerList(fieldIdsRaw);
    if (fieldIds.length) {
      out[layerId] = fieldIds;
    }
  }
  return out;
}

function normalizePatchLabel(patchId, patchName) {
  const normalizedName = String(patchName ?? "").trim();
  return normalizedName || patchId;
}

function normalizePatchStartTs(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function computeNormalizedPatchCatalog(patches) {
  return patches
    .flatMap((patch) => {
      const patchId = normalizePatchId(patch?.patchId);
      if (!patchId) {
        return [];
      }
      return [{
        patchId,
        label: normalizePatchLabel(patchId, patch?.patchName),
        startTsUtc: normalizePatchStartTs(patch?.startTsUtc),
      }];
    })
    .sort((left, right) => {
      if (right.startTsUtc !== left.startTsUtc) {
        return right.startTsUtc - left.startTsUtc;
      }
      return right.patchId.localeCompare(left.patchId);
    });
}

function normalizePatchCatalog(patches) {
  if (!Array.isArray(patches)) {
    return EMPTY_QUERY_INPUT_ARRAY;
  }
  const cached = NORMALIZED_PATCH_CATALOG_CACHE.get(patches);
  if (cached && cached.length === patches.length) {
    return cached.value;
  }
  const value = computeNormalizedPatchCatalog(patches);
  NORMALIZED_PATCH_CATALOG_CACHE.set(patches, { length: patches.length, value });
  return value;
}

export function semanticTermLookupKey(layerId, fieldId) {
  return `${String(layerId || "").trim()}:${Number.parseInt(fieldId, 10)}`;
}

export function buildSemanticTermLookup(stateBundle) {
  return buildQueryInputSemanticTermLookup(stateBundle?.state?.catalog?.semanticTerms);
}

export function buildQueryInputSemanticTermLookup(semanticTerms) {
  const terms = Array.isArray(semanticTerms) ? semanticTerms : EMPTY_QUERY_INPUT_ARRAY;
  const cached = SEMANTIC_TERM_LOOKUP_CACHE.get(terms);
  if (cached && cached.length === terms.length) {
    return cached.value;
  }
  const value = new Map(
    terms.map((term) => [
      semanticTermLookupKey(term.layerId, term.fieldId),
      term,
    ]),
  );
  SEMANTIC_TERM_LOOKUP_CACHE.set(terms, { length: terms.length, value });
  return value;
}

function identityLookup(catalogRows, fields) {
  const lookup = new Map();
  for (const row of Array.isArray(catalogRows) ? catalogRows : []) {
    for (const field of fields) {
      const id = Number.parseInt(row?.[field], 10);
      if (Number.isInteger(id) && id > 0 && !lookup.has(id)) {
        lookup.set(id, row);
      }
    }
  }
  return lookup;
}

export function buildQueryInputFishIdentityLookup(catalogFish) {
  const fish = Array.isArray(catalogFish) ? catalogFish : EMPTY_QUERY_INPUT_ARRAY;
  const cached = FISH_IDENTITY_LOOKUP_CACHE.get(fish);
  if (cached && cached.length === fish.length) {
    return cached.value;
  }
  const value = identityLookup(fish, ["fishId", "itemId"]);
  FISH_IDENTITY_LOOKUP_CACHE.set(fish, { length: fish.length, value });
  return value;
}

export function buildQueryInputItemIdentityLookup(catalogItems) {
  const items = Array.isArray(catalogItems) ? catalogItems : EMPTY_QUERY_INPUT_ARRAY;
  const cached = ITEM_IDENTITY_LOOKUP_CACHE.get(items);
  if (cached && cached.length === items.length) {
    return cached.value;
  }
  const value = identityLookup(items, ["itemId"]);
  ITEM_IDENTITY_LOOKUP_CACHE.set(items, { length: items.length, value });
  return value;
}

export function buildQueryInputTradeNpcIdentityLookup(catalogTradeNpcs) {
  const tradeNpcs = Array.isArray(catalogTradeNpcs)
    ? catalogTradeNpcs
    : EMPTY_QUERY_INPUT_ARRAY;
  const cached = TRADE_NPC_IDENTITY_LOOKUP_CACHE.get(tradeNpcs);
  if (cached && cached.length === tradeNpcs.length) {
    return cached.value;
  }
  const value = identityLookup(tradeNpcs, ["npcKey"]);
  TRADE_NPC_IDENTITY_LOOKUP_CACHE.set(tradeNpcs, {
    length: tradeNpcs.length,
    value,
  });
  return value;
}

function normalizeSharedFishState(value) {
  const caughtIds = normalizeIntegerList(value?.caughtIds);
  const favouriteIds = normalizeIntegerList(value?.favouriteIds);
  return {
    caughtIds,
    favouriteIds,
    showDried: value?.showDried === true || value?.show_dried === true,
    caughtSet: new Set(caughtIds),
    favouriteSet: new Set(favouriteIds),
    missingTitleItemIds: value?.missingTitleItemIds || [],
    missingTitleSet: new Set(value?.missingTitleItemIds || []),
  };
}

export function buildSearchPanelStateBundle(signals, options = {}) {
  const runtime = isPlainObject(signals?._map_runtime) ? signals._map_runtime : {};
  const search = isPlainObject(signals?._map_ui?.search) ? signals._map_ui.search : {};
  const catalogProjector = typeof options.catalogProjector === "function"
    ? options.catalogProjector
    : projectSearchCatalog;
  const catalogRevision = Object.prototype.hasOwnProperty.call(options, "catalogRevision")
    ? options.catalogRevision
    : runtime.catalogRevision;
  const catalog = catalogProjector(
    isPlainObject(runtime.catalog) ? runtime.catalog : {},
    catalogRevision === undefined ? undefined : { revision: catalogRevision },
  );
  const expression = resolveSearchExpression(search.expression);
  const selectedTerms = resolveSelectedSearchTerms(
    undefined,
    expression,
  );
  const rawExpressionTree = normalizeRawSearchExpressionTree(search.rawExpressionTree);
  return {
    state: {
      ready: runtime.ready === true,
      catalog: {
        titles: options.titleProjection?.kind === "ready" ? options.titleProjection.titles : null,
        fish: catalog.fish,
        items: catalog.items,
        patches: normalizePatchCatalog(catalog.patches),
        semanticTerms: catalog.semanticTerms,
        tradeNpcs: Array.isArray(options.tradeNpcCatalog?.features)
          ? options.tradeNpcCatalog.features
          : Array.isArray(options.tradeNpcCatalog)
            ? options.tradeNpcCatalog
            : EMPTY_QUERY_INPUT_ARRAY,
        zones: Array.isArray(options.zoneCatalog)
          ? options.zoneCatalog
          : EMPTY_QUERY_INPUT_ARRAY,
      },
    },
    inputState: {
      search: {
        expression,
        selectedTerms,
        rawExpression: String(search.rawExpression ?? ""),
        ...(rawExpressionTree ? { rawExpressionTree } : {}),
        expressionIssues: normalizeSearchExpressionIssues(search.expressionIssues),
      },
    },
    titleProjection: options.titleProjection,
    sharedFishState: normalizeSharedFishState({ ...signals?._shared_fish, missingTitleItemIds: options.titleProjection?.missingTitleItemIds }),
  };
}

export function resolveSelectedSearchTermsFromBundle(stateBundle) {
  const expression = resolveSearchExpression(
    stateBundle?.inputState?.search?.expression,
  );
  return resolveSelectedSearchTerms(
    undefined,
    expression,
  );
}

export function resolveSelectedFishIds(stateBundle) {
  return normalizeIntegerList(
    resolveSelectedSearchTermsFromBundle(stateBundle)
      .filter((term) => term?.kind === "fish")
      .map((term) => term.fishId),
  );
}

export function resolveSelectedItemIds(stateBundle) {
  return normalizeIntegerList(
    resolveSelectedSearchTermsFromBundle(stateBundle).flatMap((term) => {
      if (term?.kind === "item") {
        return [term.itemId];
      }
      if (term?.kind === "item-group") {
        return Array.isArray(term.itemIds) ? term.itemIds : [];
      }
      return [];
    }),
  );
}

export function resolveSelectedSemanticFieldIdsByLayer(stateBundle) {
  const selectedByLayer = {};
  for (const term of resolveSelectedSearchTermsFromBundle(stateBundle)) {
    if (term?.kind !== "semantic") {
      continue;
    }
    const layerId = String(term.layerId ?? "").trim();
    const fieldId = Number.parseInt(term.fieldId, 10);
    if (!layerId || !Number.isInteger(fieldId)) {
      continue;
    }
    if (!Array.isArray(selectedByLayer[layerId])) {
      selectedByLayer[layerId] = [];
    }
    selectedByLayer[layerId].push(fieldId);
  }
  return normalizeSemanticFieldIdsByLayer(selectedByLayer);
}

function tradeNpcQueryInputText(npc, tradeNpcs) {
  const npcKey = Number.parseInt(npc?.npcKey, 10);
  if (!Number.isInteger(npcKey) || npcKey <= 0) {
    return "";
  }
  const npcName = String(npc?.npcName ?? "").trim().replace(/\s+/g, " ");
  if (!npcName) {
    return `npc:${npcKey}`;
  }
  const resolved = resolveSearchExpressionTradeNpcKey(npcName, { tradeNpcs });
  if (resolved.status !== "resolved" || resolved.npcKey !== npcKey) {
    return `npc:${npcKey}`;
  }
  const normalizedName = npcName.toLocaleLowerCase();
  const selector = /^[\p{L}\p{N}_.-]+$/u.test(normalizedName)
    ? normalizedName
    : quotedQueryInputValue(normalizedName);
  return `npc:${selector}`;
}

function resolveSelectedTradeNpcKeys(stateBundle) {
  return new Set(
    resolveSelectedSearchTermsFromBundle(stateBundle)
      .map(tradeNpcKeyFromSearchTerm)
      .filter((npcKey) => npcKey != null),
  );
}

function queryInputCatalogFromBundle(stateBundle) {
  const items = Array.isArray(stateBundle?.state?.catalog?.items)
    ? stateBundle.state.catalog.items
    : [];
  return {
    titles: stateBundle?.state?.catalog?.titles,
    fish: Array.isArray(stateBundle?.state?.catalog?.fish)
      ? stateBundle.state.catalog.fish
      : [],
    items,
    patches: Array.isArray(stateBundle?.state?.catalog?.patches)
      ? stateBundle.state.catalog.patches
      : [],
    semanticTerms: Array.isArray(stateBundle?.state?.catalog?.semanticTerms)
      ? stateBundle.state.catalog.semanticTerms
      : [],
    tradeNpcs: Array.isArray(stateBundle?.state?.catalog?.tradeNpcs)
      ? stateBundle.state.catalog.tradeNpcs
      : [],
    zones: Array.isArray(stateBundle?.state?.catalog?.zones)
      ? stateBundle.state.catalog.zones
      : [],
    specialItemReferences: specialItemReferencesForCatalogItems(items),
  };
}

function alignedPreparedValueAssignments(entries, preparedValues, cache, label) {
  if (!Array.isArray(preparedValues) || entries.length !== preparedValues.length) {
    throw new TypeError(`${label} prepared values must align with the catalog revision`);
  }
  return entries.map((entry, index) => ({
    cache,
    entry,
    values: preparedValues[index],
    label: `${label}[${index}]`,
  }));
}

export function createQueryInputCatalogPreparedValuesTransaction(stateBundle, prepared) {
  const catalog = queryInputCatalogFromBundle(stateBundle);
  const assignments = alignedPreparedValueAssignments(
    catalog.fish,
    prepared?.fishSearch,
    FISH_PREPARED_SEARCH_VALUES_CACHE,
    "fish search",
  );
  assignments.push(...alignedPreparedValueAssignments(
    catalog.semanticTerms,
    prepared?.semanticSearch,
    SEMANTIC_PREPARED_SEARCH_VALUES_CACHE,
    "semantic search",
  ));

  const normalizedItems = normalizeCatalogItems(catalog.items);
  const preparedItems = Array.isArray(prepared?.itemSearch) ? prepared.itemSearch : [];
  const preparedItemsById = new Map();
  for (const item of preparedItems) {
    const itemId = Number.parseInt(item?.itemId, 10);
    if (!Number.isInteger(itemId) || preparedItemsById.has(itemId)) {
      throw new TypeError("item search prepared values must have unique integer item IDs");
    }
    preparedItemsById.set(itemId, item?.values);
  }
  if (
    preparedItemsById.size !== normalizedItems.length ||
    normalizedItems.some((item) => !preparedItemsById.has(item.itemId))
  ) {
    throw new TypeError("item search prepared values must align with the catalog revision");
  }
  assignments.push(...normalizedItems.map((item, index) => ({
    cache: ITEM_PREPARED_SEARCH_VALUES_CACHE,
    entry: item,
    values: preparedItemsById.get(item.itemId),
    label: `item search[${index}]`,
  })));
  return createPreparedQueryMatchCacheTransaction(assignments);
}

export function prepareQueryInputCatalogIndexes(stateBundle) {
  const startedAt = globalThis.performance?.now?.() ?? null;
  const catalog = queryInputCatalogFromBundle(stateBundle);
  const normalizedItems = normalizeCatalogItems(catalog.items);
  const indexes = {
    fish: FISH_CATALOG_INDEX(catalog.fish),
    itemSearch: ITEM_SEARCH_CATALOG_INDEX(normalizedItems),
    patches: PATCH_CATALOG_INDEX(catalog.patches),
    semanticTerms: SEMANTIC_CATALOG_INDEX(catalog.semanticTerms),
    tradeNpcs: TRADE_NPC_CATALOG_INDEX(catalog.tradeNpcs),
    specialItems: SPECIAL_ITEM_CATALOG_INDEX(catalog.specialItemReferences),
    zones: ZONE_CATALOG_INDEX(catalog.zones),
  };
  buildQueryInputFishIdentityLookup(catalog.fish);
  buildQueryInputItemIdentityLookup(catalog.items);
  buildQueryInputItemIdentityLookup(normalizedItems);
  buildQueryInputSemanticTermLookup(catalog.semanticTerms);
  buildQueryInputTradeNpcIdentityLookup(catalog.tradeNpcs);
  plainCatalogPhraseLookupValues(stateBundle);
  const expression = prepareSearchExpressionCatalogIndexes(catalog);
  const durationMs = startedAt == null
    ? null
    : Math.max(0, globalThis.performance.now() - startedAt);
  return Object.freeze({
    durationMs,
    indexedRecords: Object.freeze(Object.fromEntries(
      Object.entries(indexes).map(([name, index]) => [name, index.records.length]),
    )),
    expression,
  });
}

export function buildQueryInputCatalogLookups(stateBundle) {
  const catalog = queryInputCatalogFromBundle(stateBundle);
  return {
    catalog,
    lookups: {
      fish: buildQueryInputFishIdentityLookup(catalog.fish),
      items: buildQueryInputItemIdentityLookup(catalog.items),
      semantic: buildQueryInputSemanticTermLookup(catalog.semanticTerms),
      tradeNpcs: buildQueryInputTradeNpcIdentityLookup(catalog.tradeNpcs),
      specialItemReferences: Array.isArray(catalog.specialItemReferences)
        ? catalog.specialItemReferences
        : EMPTY_QUERY_INPUT_ARRAY,
    },
  };
}

function normalizeDraftText(value) {
  return String(value ?? "");
}

function normalizeCaretIndex(value, text) {
  const length = String(text ?? "").length;
  const caret = Number.parseInt(value, 10);
  if (!Number.isFinite(caret)) {
    return length;
  }
  return Math.max(0, Math.min(length, caret));
}

function draftTokenReplacementSpan(text, caretIndex) {
  const source = normalizeDraftText(text);
  const caret = normalizeCaretIndex(caretIndex, source);
  let start = caret;
  while (start > 0 && !/[\s()]/.test(source[start - 1])) {
    start -= 1;
  }
  let end = caret;
  while (end < source.length && !/[\s()]/.test(source[end])) {
    end += 1;
  }
  return {
    start,
    end,
    token: source.slice(start, end),
    before: source.slice(0, start),
    after: source.slice(end),
  };
}

function draftSpacedValueReplacementSpan(text, caretIndex) {
  const source = normalizeDraftText(text);
  const caret = normalizeCaretIndex(caretIndex, source);
  const beforeCaret = source.slice(0, caret);
  const match = beforeCaret.match(/(^|[\s(])((?:fish|item|npc|semantic|patch|from|to|zone|region|regions|regiongroup|regiongroups|region_group|region_groups):[^\n()]*)$/i);
  if (!match) {
    return null;
  }
  const token = match[2] || "";
  const colonIndex = token.indexOf(":");
  const prefix = token.slice(0, colonIndex).toLowerCase().replace(/-/g, "_");
  const rawValue = token.slice(colonIndex + 1);
  if (!QUERY_INPUT_SPACED_VALUE_PREFIXES.has(prefix) || !/\s/.test(rawValue)) {
    return null;
  }
  if (/^\d+(?:\s+|$)/.test(rawValue)) {
    return null;
  }
  let end = caret;
  while (end < source.length && !/[\n()]/.test(source[end])) {
    end += 1;
  }
  const start = beforeCaret.length - token.length;
  return {
    start,
    end,
    token: source.slice(start, end),
    before: source.slice(0, start),
    after: source.slice(end),
  };
}

function queryInputDslTokenIsValue(token) {
  return token?.type === "word" || token?.type === "value";
}

function queryInputDslTokenIsStandaloneOperator(token) {
  if (token?.type !== "word") {
    return false;
  }
  const lowered = searchExpressionTokenLower(token);
  return lowered === "and" || lowered === "or" || lowered === "not";
}

function booleanOperatorSuggestion(replacement) {
  const source = String(replacement?.token || "");
  const tokens = tokenizeSearchExpressionDsl(source);
  if (
    tokens.length !== 1 ||
    tokens[0].start !== 0 ||
    tokens[0].end !== source.length ||
    tokens[0].type !== "word"
  ) {
    return null;
  }
  const canonicalPrefix = searchExpressionTokenLower(tokens[0]).toUpperCase();
  const isCompleteOperator = queryInputDslTokenIsStandaloneOperator(tokens[0]);
  if (!isCompleteOperator && !String(replacement?.before || "").trim()) {
    return null;
  }
  const matches = QUERY_INPUT_BOOLEAN_OPERATOR_SUGGESTIONS.filter(
    (candidate) => candidate.insertText.startsWith(canonicalPrefix),
  );
  if (!canonicalPrefix || matches.length !== 1) {
    return null;
  }
  const descriptor = matches[0];
  return descriptor
    ? queryInputSuggestion({
        ...descriptor,
        replacement,
        exactMatchInput: source,
        exactMatchValues: [descriptor.insertText, descriptor.insertText.toLowerCase()],
      })
    : null;
}

function normalizePlainCatalogPhraseText(value) {
  return String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

function catalogArrayCacheEntry(value) {
  const ref = Array.isArray(value) ? value : EMPTY_QUERY_INPUT_ARRAY;
  return { ref, length: ref.length };
}

function catalogArrayCacheEntryMatches(entry, value) {
  const ref = Array.isArray(value) ? value : EMPTY_QUERY_INPUT_ARRAY;
  return entry?.ref === ref && entry.length === ref.length;
}

function plainCatalogPhraseLookupCacheMatches(catalog) {
  const entry = plainCatalogPhraseLookupCache;
  return Boolean(entry) &&
    catalogArrayCacheEntryMatches(entry.fish, catalog.fish) &&
    catalogArrayCacheEntryMatches(entry.items, catalog.items) &&
    catalogArrayCacheEntryMatches(entry.zones, catalog.zones) &&
    catalogArrayCacheEntryMatches(entry.semanticTerms, catalog.semanticTerms) &&
    catalogArrayCacheEntryMatches(entry.tradeNpcs, catalog.tradeNpcs) &&
    catalogArrayCacheEntryMatches(entry.patches, catalog.patches) &&
    catalogArrayCacheEntryMatches(entry.specialItemReferences, catalog.specialItemReferences);
}

function plainCatalogPhraseLookupValues(stateBundle) {
  const catalog = queryInputCatalogFromBundle(stateBundle);
  if (plainCatalogPhraseLookupCacheMatches(catalog)) {
    return plainCatalogPhraseLookupCache.values;
  }
  const values = [
    ...catalog.fish.flatMap((fish) => [
      fish?.name,
    ]),
    ...catalog.items.flatMap((item) => [
      item?.name,
      ...(Array.isArray(item?.labels) ? item.labels : []),
    ]),
    ...catalog.zones.flatMap((zone) => [
      zone?.name,
    ]),
    ...catalog.semanticTerms.flatMap((term) => [
      term?.layerName,
      term?.label,
      term?.description,
      term?.searchText,
    ]),
    ...catalog.tradeNpcs.flatMap((npc) => [
      npc?.npcName,
      npc?.assignedRegionLabel,
      npc?.sellOriginLabel,
    ]),
    ...catalog.patches.flatMap((patch) => [
      patch?.patchId,
      patch?.label,
    ]),
    ...catalog.specialItemReferences.flatMap((entry) => [
      entry?.name,
      entry?.label,
      ...(Array.isArray(entry?.labels) ? entry.labels : []),
      ...(Array.isArray(entry?.lookupValues) ? entry.lookupValues : []),
    ]),
  ].filter((value) => String(value ?? "").trim());
  plainCatalogPhraseLookupCache = {
    fish: catalogArrayCacheEntry(catalog.fish),
    items: catalogArrayCacheEntry(catalog.items),
    zones: catalogArrayCacheEntry(catalog.zones),
    semanticTerms: catalogArrayCacheEntry(catalog.semanticTerms),
    tradeNpcs: catalogArrayCacheEntry(catalog.tradeNpcs),
    patches: catalogArrayCacheEntry(catalog.patches),
    specialItemReferences: catalogArrayCacheEntry(catalog.specialItemReferences),
    values,
  };
  return values;
}

function plainSearchPhraseMatchesCatalog(stateBundle, rawPhrase) {
  const phrase = normalizePlainCatalogPhraseText(rawPhrase);
  if (!phrase || !/\s/.test(phrase)) {
    return false;
  }
  return plainCatalogPhraseLookupValues(stateBundle).some((value) => {
    const candidate = normalizePlainCatalogPhraseText(value);
    return candidate === phrase || candidate.startsWith(phrase) || candidate.includes(phrase);
  });
}

function draftPrefixParsesAsExpression(stateBundle, rawPrefix) {
  const prefix = String(rawPrefix ?? "").trim();
  if (!prefix) {
    return false;
  }
  const parsed = parseSearchExpressionDslResult(prefix, {
    catalog: queryInputCatalogFromBundle(stateBundle),
    includeRawExpressionTree: false,
  });
  return Boolean(parsed.expression) &&
    !(parsed.issues || []).some((issue) => issue?.severity !== "warning");
}

function draftPlainSearchValueTokens(source, start, end) {
  return tokenizeSearchExpressionDsl(source)
    .filter((token) =>
      token.start >= start &&
      token.end <= end &&
      queryInputDslTokenIsValue(token) &&
      !queryInputDslTokenIsStandaloneOperator(token)
    );
}

function adjustedDraftPlainSearchStart(stateBundle, source, start, end) {
  const rawPhrase = source.slice(start, end).trim();
  if (plainSearchPhraseMatchesCatalog(stateBundle, rawPhrase)) {
    return start;
  }
  const tokens = draftPlainSearchValueTokens(source, start, end);
  if (tokens.length <= 1) {
    return start;
  }
  let fallbackStart = start;
  for (let index = 1; index < tokens.length; index += 1) {
    const token = tokens[index];
    const prefix = source.slice(start, token.start);
    const suffix = source.slice(token.start, end);
    if (!/\p{L}/u.test(suffix) || !draftPrefixParsesAsExpression(stateBundle, prefix)) {
      continue;
    }
    if (plainSearchPhraseMatchesCatalog(stateBundle, suffix)) {
      return token.start;
    }
    fallbackStart = token.start;
  }
  return fallbackStart;
}

function draftPlainSearchReplacementSpan(stateBundle, text, caretIndex) {
  const source = normalizeDraftText(text);
  const caret = normalizeCaretIndex(caretIndex, source);
  const beforeCaret = source.slice(0, caret);
  let start = Math.max(
    beforeCaret.lastIndexOf("("),
    beforeCaret.lastIndexOf(")"),
    beforeCaret.lastIndexOf("\n"),
  ) + 1;
  const operatorPattern = /(?:^|[\s(])(?:AND|OR|NOT)\s+/gi;
  let operatorMatch;
  while ((operatorMatch = operatorPattern.exec(beforeCaret.slice(start))) !== null) {
    start += operatorMatch.index + operatorMatch[0].length;
    operatorPattern.lastIndex = 0;
  }
  while (start < caret && /\s/.test(source[start])) {
    start += 1;
  }
  let end = caret;
  while (end < source.length && !/[\n()]/.test(source[end])) {
    end += 1;
  }
  let token = source.slice(start, end).trim();
  if (
    !/\p{L}/u.test(token) ||
    !/\S+\s+\S/.test(token) ||
    /\b(?:AND|OR|NOT)\b/i.test(token)
  ) {
    return null;
  }
  start = adjustedDraftPlainSearchStart(stateBundle, source, start, end);
  while (start < end && /\s/.test(source[start])) {
    start += 1;
  }
  token = source.slice(start, end).trim();
  if (
    !/\p{L}/u.test(token) ||
    !/\S+\s+\S/.test(token) ||
    token.includes(":") ||
    /\b(?:AND|OR|NOT)\b/i.test(token)
  ) {
    return null;
  }
  return {
    start,
    end,
    token: source.slice(start, end),
    before: source.slice(0, start),
    after: source.slice(end),
  };
}

function normalizedSuggestionNeedle(value) {
  return normalizeQueryMatchText(value).replace(/_/g, "-");
}

function indexedCatalogEntries(buildIndex, entries, query, options = {}) {
  const index = buildIndex(Array.isArray(entries) ? entries : EMPTY_QUERY_INPUT_ARRAY);
  const needle = String(query ?? "").trim();
  const records = options.prefixOnly === true && needle
    ? queryCatalogPrefixCandidates(index, needle)
    : options.suggestion === true && needle && compactQueryMatchText(needle).length <= 1
    ? queryCatalogPrefixCandidates(index, needle)
    : queryCatalogCandidates(index, needle);
  return records.map((record) => record.value);
}

function indexedOneCharacterEntries(
  buildIndex,
  entries,
  query,
  limit,
  prefixOnly,
  includeEntry = null,
) {
  const index = buildIndex(Array.isArray(entries) ? entries : EMPTY_QUERY_INPUT_ARRAY);
  return queryCatalogOneCharacterCandidates(index, query, {
    limit,
    prefixOnly,
    includeRecord: includeEntry
      ? (record) => includeEntry(record.value)
      : null,
  }).map((record) => record.value);
}

function indexedZoneCatalogEntries(entries, query, options = {}) {
  const catalog = Array.isArray(entries) ? entries : EMPTY_QUERY_INPUT_ARRAY;
  const index = ZONE_CATALOG_INDEX(catalog);
  const needle = String(query ?? "").trim();
  const boundedOneCharacter =
    needle && compactQueryMatchText(needle).length === 1 &&
    Number.parseInt(options.limit, 10) > 0;
  const values = boundedOneCharacter
    ? indexedOneCharacterEntries(
        ZONE_CATALOG_INDEX,
        catalog,
        needle,
        options.limit,
        options.prefixOnly === true,
        options.includeEntry,
      )
    : null;
  const records = values
    ? []
    : options.prefixOnly === true && needle
      ? queryCatalogPrefixCandidates(index, needle)
      : queryCatalogCandidates(index, needle);
  const zoneRgb = parseZoneRgbSearch(query);
  if (zoneRgb == null) {
    return values || records.map((record) => record.value);
  }
  const seen = new Set(values || records.map((record) => record.value));
  for (const record of queryCatalogExactCandidates(index, String(zoneRgb))) {
    seen.add(record.value);
  }
  return [...seen];
}

function patchCatalogIndexValues(patch) {
  return [
    patch?.patchId,
    patch?.label,
  ];
}

function zoneCatalogIndexValues(zone) {
  return [
    zone?.zoneRgb,
    zone?.name,
    zone?._nameSearch,
    zone?.rgbKey,
    zone?.rgbSpaced,
    zone?.normalizedKey,
    zone?.normalizedSpaced,
    zone?.hexKey,
    zone?.hashHexKey,
    zone?.bareHexKey,
  ];
}

function zoneNameSearchValues(zone) {
  return [zone?.name, zone?._nameSearch];
}

function suggestionMatchesNeedle(needle, values) {
  if (!needle) {
    return true;
  }
  const match = matchQueryValues(values, needle);
  if (compactQueryMatchText(needle).length <= 1) {
    return match.rank >= QUERY_MATCH_RANK_PREFIX;
  }
  return match.rank > QUERY_MATCH_RANK_NONE;
}

function queryInputSuggestion({
  kind,
  label,
  description = "",
  insertText,
  replacement,
  exactMatchInput = "",
  exactMatchValues = [],
  previewText = "",
  catalogKind = "",
  catalogEntry = null,
  score = 0,
  preparedRanking = null,
}) {
  if (!insertText || !replacement) {
    return null;
  }
  const ranking = preparedRanking || rankQueryInputSuggestion({
    label,
    insertText,
    exactMatchInput,
    exactMatchValues,
    previewText,
    score,
  });
  return {
    kind: String(kind || "term"),
    label: String(label || insertText),
    description: String(description || ""),
    insertText: String(insertText),
    replaceStart: replacement.start,
    replaceEnd: replacement.end,
    exactMatchValues: ranking.exactMatchValues,
    previewText: String(previewText || ""),
    catalogKind: String(catalogKind || ""),
    catalogEntry: cloneCatalogSuggestionEntry(catalogEntry),
    matchRank: ranking.matchRank,
    matchMode: ranking.matchMode,
    matchedValue: ranking.matchedValue,
    windowStart: ranking.windowStart,
    windowEnd: ranking.windowEnd,
    score: ranking.score,
  };
}

function cloneCatalogSuggestionEntry(entry) {
  if (!isPlainObject(entry)) {
    return null;
  }
  const cloned = {};
  for (const [key, value] of Object.entries(entry)) {
    if (
      value == null ||
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      cloned[key] = value;
    } else if (Array.isArray(value)) {
      cloned[key] = value.filter((entryValue) =>
        entryValue == null ||
        typeof entryValue === "string" ||
        typeof entryValue === "number" ||
        typeof entryValue === "boolean"
      );
    }
  }
  return cloned;
}

function sortQueryInputSuggestions(suggestions, options = {}) {
  return selectBoundedQueryInputCandidates(
    suggestions,
    options.limit ?? QUERY_INPUT_SUGGESTION_LIMIT,
  ).map(({ _ordinal: _ignoredOrdinal, ...suggestion }) => suggestion);
}

function specialItemReferenceSuggestionLabels(reference) {
  return [
    reference?.aliasItemId,
    reference?.name,
    reference?.label,
    ...(Array.isArray(reference?.labels) ? reference.labels : []),
    ...(Array.isArray(reference?.lookupValues) ? reference.lookupValues : []),
  ];
}

function buildSpecialItemQueryInputSuggestions(stateBundle, replacement, rawValue, scoreBase = 245) {
  const needle = normalizedSuggestionNeedle(rawValue);
  const references = specialItemReferencesForCatalogItems(stateBundle?.state?.catalog?.items || []);
  return indexedCatalogEntries(SPECIAL_ITEM_CATALOG_INDEX, references, needle, { suggestion: true })
    .filter((reference) => suggestionMatchesNeedle(needle, specialItemReferenceSuggestionLabels(reference)))
    .map((reference, index) => {
      const itemId = Number.parseInt(reference?.itemId, 10);
      if (!Number.isInteger(itemId) || itemId <= 0) {
        return null;
      }
      const exactMatchValues = specialItemReferenceSuggestionLabels(reference);
      const insertText = searchMatchQueryInputText(reference);
      if (!insertText) {
        return null;
      }
      return queryInputSuggestion({
        kind: reference?.kind === "item-group" ? "item-group" : "item",
        label: reference?.name || reference?.label || `Item ${itemId}`,
        description: insertText,
        insertText,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues,
        catalogKind: reference?.kind === "item-group" ? "item-group" : "item",
        catalogEntry: catalogEntryForSearchMatch(reference),
        score: scoreBase - index,
      });
    });
}

function patchBoundQueryPrefix(bound) {
  const normalized = normalizePatchBound(bound);
  if (normalized === "from") {
    return "after";
  }
  if (normalized === "to") {
    return "before";
  }
  return normalized || "patch";
}

function semanticQueryInputPrefix(term) {
  const layerId = String(term?.layerId || "").trim();
  if (layerId === "regions") {
    return "region";
  }
  if (layerId === "region_groups") {
    return "regiongroup";
  }
  return "semantic";
}

function searchMatchQueryInputText(match, tradeNpcs) {
  if (!match || typeof match !== "object") {
    return "";
  }
  if (match.kind === "status") {
    const status = normalizeSearchStatusTerm(match.status ?? match.term);
    return status ? `status:${status}` : "";
  }
  if (match.kind === "grade") {
    const grade = normalizeSearchGradeTerm(match.grade ?? match.term);
    return grade ? `grade:${grade}` : "";
  }
  if (match.kind === "patch-bound") {
    const prefix = patchBoundQueryPrefix(match.bound);
    const patchId = normalizePatchId(match.patchId);
    return patchId ? `${prefix}:${patchId}` : `${prefix}:`;
  }
  if (match.kind === "fish") {
    const fishReferenceId = Number.parseInt(match.itemId ?? match.fishId, 10);
    return Number.isInteger(fishReferenceId) && fishReferenceId > 0
      ? `fish:${fishReferenceId}`
      : "";
  }
  if (match.kind === "item") {
    const itemId = Number.parseInt(match.itemId, 10);
    return Number.isInteger(itemId) && itemId > 0
      ? `item:${itemId}`
      : "";
  }
  if (match.kind === "item-group") {
    const itemIds = normalizeIntegerList(match.itemIds);
    const aliasItemId = Number.parseInt(match.aliasItemId, 10);
    if (Number.isInteger(aliasItemId) && aliasItemId > 0) {
      return `item:${aliasItemId}`;
    }
    if (!itemIds.length) {
      return "";
    }
    const itemGroup = `items:[${itemIds.join(",")}]`;
    const sourceKinds = sourceKindsForResultRows(match.sourceKinds);
    return sourceKinds.length
      ? `${itemGroup}@${sourceKinds.join(",")}`
      : itemGroup;
  }
  if (match.kind === "zone") {
    return zoneSuggestionInsertText(match, formatZoneSuggestionRgb(match.zoneRgb));
  }
  if (match.kind === "semantic") {
    const layerId = String(match.layerId || "").trim();
    const fieldId = Number.parseInt(match.fieldId, 10);
    if (!layerId || !Number.isInteger(fieldId)) {
      return "";
    }
    const prefix = semanticQueryInputPrefix(match);
    return prefix === "semantic" ? `semantic:${layerId}:${fieldId}` : `${prefix}:${fieldId}`;
  }
  if (match.kind === "trade-npc") {
    return tradeNpcQueryInputText(match, tradeNpcs);
  }
  return "";
}

function searchMatchSuggestionDedupeKey(match, tradeNpcs) {
  if (!match || typeof match !== "object") {
    return "";
  }
  const cached = SEARCH_MATCH_SUGGESTION_KEY_CACHE.get(match);
  if (cached != null) {
    return cached;
  }
  const key = searchMatchQueryInputText(match, tradeNpcs);
  SEARCH_MATCH_SUGGESTION_KEY_CACHE.set(match, key);
  return key;
}

function searchMatchSuggestionDescription(match, insertText) {
  if (match?.kind === "status") {
    return "fish status";
  }
  if (match?.kind === "grade") {
    return "fish grade";
  }
  if (match?.kind === "fish") {
    return insertText;
  }
  if (match?.kind === "item") {
    const sources = sourceKindsForResultRows(match.sourceKinds);
    return sources.length ? `${insertText} · ${sources.join(", ")}` : insertText;
  }
  if (match?.kind === "item-group") {
    const itemCount = normalizeIntegerList(match.itemIds).length;
    const sources = sourceKindsForResultRows(match.sourceKinds);
    return [itemCount ? `${itemCount} item${itemCount === 1 ? "" : "s"}` : "", sources.join(", ")]
      .filter(Boolean)
      .join(" · ");
  }
  if (match?.kind === "zone") {
    return "fishing zone";
  }
  if (match?.kind === "semantic") {
    return [match.layerName || match.layerId || "map", match.description || ""]
      .filter(Boolean)
      .join(" · ");
  }
  if (match?.kind === "trade-npc") {
    return ["Trade NPC", match.assignedRegionLabel || match.sellOriginLabel || ""]
      .filter(Boolean)
      .join(" · ");
  }
  if (match?.kind === "patch-bound") {
    return match.description || insertText;
  }
  return String(match?.description || insertText || "").trim();
}

function searchMatchExactValues(match) {
  if (match?.kind === "fish") {
    return [match.fishId, match.itemId, match.name, match.grade];
  }
  if (match?.kind === "item") {
    return itemSuggestionLabels(match);
  }
  if (match?.kind === "item-group") {
    return [
      match.itemId,
      match.name,
      match.label,
      ...(Array.isArray(match.labels) ? match.labels : []),
      ...(Array.isArray(match.itemIds) ? match.itemIds : []),
    ];
  }
  if (match?.kind === "zone") {
    return [
      match.name,
      match.rgbKey,
      match.rgbSpaced,
      match.hexKey,
      match.hashHexKey,
      match.bareHexKey,
      formatZoneSuggestionRgb(match.zoneRgb),
    ];
  }
  if (match?.kind === "semantic") {
    return [match.layerId, match.layerName, match.fieldId, match.label];
  }
  if (match?.kind === "trade-npc") {
    return [
      match.npcKey,
      match.npcName,
      match.assignedRegionLabel,
      match.sellOriginLabel,
      ...(Array.isArray(match.sourceTags) ? match.sourceTags : []),
    ];
  }
  if (match?.kind === "patch-bound") {
    return [match.bound, match.label, match.patchId, match.description];
  }
  return [match?.term, match?.status, match?.grade, match?.label];
}

function catalogEntryForSearchMatch(match) {
  if (!match || typeof match !== "object") {
    return null;
  }
  if (match.kind === "fish") {
    return {
      fishId: match.fishId,
      itemId: match.itemId,
      name: match.name,
      grade: match.grade,
      isPrize: match.isPrize === true,
      iconItemId: match.iconItemId,
      iconImage: match.iconImage,
      encyclopediaId: match.encyclopediaId,
      encyclopediaIconImage: match.encyclopediaIconImage,
    };
  }
  if (match.kind === "item" || match.kind === "item-group") {
    return {
      itemId: match.itemId,
      itemIds: normalizeIntegerList(match.itemIds),
      name: match.name,
      label: match.label,
      sourceKinds: sourceKindsForResultRows(match.sourceKinds),
      grade: match.grade,
      isPrize: match.isPrize === true,
      isFish: match.isFish === true,
      iconItemId: match.iconItemId,
      iconImage: match.iconImage,
    };
  }
  if (match.kind === "zone") {
    return {
      zoneRgb: match.zoneRgb,
      name: match.name,
      r: match.r,
      g: match.g,
      b: match.b,
      rgbKey: match.rgbKey,
      hexKey: match.hexKey,
    };
  }
  if (match.kind === "semantic") {
    return {
      layerId: match.layerId,
      layerName: match.layerName,
      fieldId: match.fieldId,
      label: match.label,
      description: match.description,
    };
  }
  if (match.kind === "trade-npc") {
    return {
      npcKey: match.npcKey,
      npcName: match.npcName,
      imagePath: match.imagePath,
      class: match.class,
      classLabel: match.classLabel,
      layerId: match.layerId,
      assignedRegionLabel: match.assignedRegionLabel,
      sellOriginLabel: match.sellOriginLabel,
    };
  }
  if (match.kind === "status" || match.kind === "grade") {
    return {
      term: match.term,
      status: match.status,
      grade: match.grade,
      label: match.label,
    };
  }
  if (match.kind === "patch-bound") {
    return {
      bound: match.bound,
      patchId: match.patchId,
      label: match.label,
    };
  }
  return null;
}

function catalogKindForSearchMatch(match) {
  if (match?.kind === "item-group") {
    return "item-group";
  }
  if (match?.kind === "status" || match?.kind === "grade") {
    return "filter";
  }
  if (match?.kind === "patch-bound") {
    return "patch";
  }
  return String(match?.kind || "");
}

function buildPlainSearchResultQueryInputSuggestions(stateBundle, replacement) {
  const query = String(replacement?.token || "").trim();
  if (!query) {
    return [];
  }
  const queryNeedle = normalizedSuggestionNeedle(query);
  const zoneCatalog = stateBundle?.state?.catalog?.zones || [];
  const tradeNpcs = stateBundle?.state?.catalog?.tradeNpcs || [];
  const dedupeKey = (match) => searchMatchSuggestionDedupeKey(match, tradeNpcs);
  let matches = buildSearchMatchesWithOptions(
    stateBundle,
    query,
    zoneCatalog,
    {
      limit: QUERY_INPUT_SUGGESTION_LIMIT,
      prefixOnly: true,
      dedupeKey,
    },
  );
  if (matches.length < QUERY_INPUT_SUGGESTION_LIMIT) {
    matches = buildSearchMatchesWithOptions(
      stateBundle,
      query,
      zoneCatalog,
      {
        limit: QUERY_INPUT_SUGGESTION_LIMIT,
        dedupeKey,
      },
    );
  }
  const candidates = (function* buildCandidates() {
    let suggestionIndex = 0;
    for (const match of matches) {
      if (
        match?.kind === "patch-bound" &&
        !normalizePatchId(match.patchId) &&
        !suggestionMatchesNeedle(queryNeedle, [
          match.bound,
          patchBoundQueryPrefix(match.bound),
          match.label,
        ])
      ) {
        continue;
      }
      const index = suggestionIndex;
      suggestionIndex += 1;
      const insertText = searchMatchQueryInputText(match, tradeNpcs);
      if (!insertText) {
        continue;
      }
      const label = String(match?.name || match?.label || insertText);
      const baseScore = 500
        + searchMatchRank(match) * 500
        - searchMatchPriority(match) * 5
        + Math.max(-200, Math.min(Number(match?._score) || 0, 400))
        - index / 1000;
      yield {
        label,
        insertText,
        replaceStart: replacement.start,
        replaceEnd: replacement.end,
        ...rankQueryInputSuggestion({
          label,
          insertText,
          exactMatchInput: query,
          exactMatchValues: searchMatchExactValues(match),
          previewText: insertText,
          score: baseScore,
        }),
        _ordinal: index,
        _baseScore: baseScore,
        _match: match,
      };
    }
  })();

  return selectBoundedQueryInputCandidates(candidates).map((candidate) => {
    const match = candidate._match;
    const insertText = candidate.insertText;
    const isSpecialItemGroup = match?.kind === "item-group"
      && Number.isInteger(Number.parseInt(match?.aliasItemId, 10));
    return queryInputSuggestion({
      kind: isSpecialItemGroup
        ? "item"
        : (match?.kind === "status" || match?.kind === "grade")
        ? match.kind
        : `result-${match?.kind || "term"}`,
      label: candidate.label,
      description: searchMatchSuggestionDescription(match, insertText),
      insertText,
      previewText: insertText,
      replacement,
      exactMatchInput: query,
      exactMatchValues: candidate.exactMatchValues,
      catalogKind: catalogKindForSearchMatch(match),
      catalogEntry: catalogEntryForSearchMatch(match),
      score: candidate._baseScore,
      preparedRanking: candidate,
    });
  });
}

function buildGeneralQueryInputSuggestions(stateBundle, replacement) {
  const needle = normalizedSuggestionNeedle(replacement.token);
  const bareInput = !/^["']/.test(replacement.token);
  const metadata = fishFilterMetadata();
  const catalogSuggestions = buildPlainSearchResultQueryInputSuggestions(stateBundle, replacement);
  const fishFilters = QUERY_INPUT_DEFAULT_FILTER_TERMS.map((term, index) => ({
    kind: FISH_GRADE_FILTER_TERMS.has(term) ? "grade" : "status",
    label: metadata[term]?.label || term,
    description: metadata[term]?.description || "",
    insertText: FISH_GRADE_FILTER_TERMS.has(term) ? `grade:${term}` : `status:${term}`,
    aliases: [
      ...fishFilterSearchValues(term, metadata[term]),
      `grade:${term}`,
      `status:${term}`,
    ],
    score: 320 - index,
  }));
  const structural = [
    { kind: "fishing-contest", label: siteText("contest.query_title"), insertText: "contest", aliases: ["contest", "weekly contest", "fishing contest", siteText("contest.query_title")], catalogKind: "filter", catalogEntry: { kind: "fishing-contest" }, score: 321 },
    { kind: "prefix", label: "title:", description: siteText("map.search.title.description"), insertText: "title:", aliases: ["title", "titles"], score: 316 },
    { kind: "prefix", label: "fish:", description: "fish or item by ID or name", insertText: "fish:", aliases: ["fish", "item"], score: 314 },
    { kind: "prefix", label: "npc:", description: "Trade NPC by ID or name", insertText: "npc:", aliases: ["npc", "trade npc", "trade manager"], score: 290 },
    { kind: "prefix", label: "after:", description: "evidence after patch", insertText: "after:", aliases: ["after"], score: 313 },
    { kind: "prefix", label: "before:", description: "evidence before patch", insertText: "before:", aliases: ["before"], score: 312 },
    ...QUERY_INPUT_BOOLEAN_OPERATOR_SUGGESTIONS,
    { kind: "prefix", label: "filter:", description: "layer-specific filter", insertText: "filter:", aliases: ["filter", "layer"], score: 308 },
  ];
  const defaultSuggestions = fishFilters.concat(structural)
    .filter((candidate) => bareInput && suggestionMatchesNeedle(needle, candidate.aliases))
    .map((candidate, index) =>
      queryInputSuggestion({
        ...candidate,
        replacement,
        exactMatchInput: replacement.token,
        exactMatchValues: candidate.aliases,
        score: candidate.score ?? 120 - index,
      }),
    );
  return sortQueryInputSuggestions(
    catalogSuggestions
      .concat(buildTitleQueryInputSuggestions(stateBundle, replacement, replacement.token), defaultSuggestions, buildSpecialItemQueryInputSuggestions(stateBundle, replacement, replacement.token, 315)),
  );
}

function buildTitleQueryInputSuggestions(bundle, replacement, value) {
  return matchingFishingTitles(bundle.state?.catalog?.titles, value)
    .filter(title => title.itemId !== null || title.titleId === GOD_OF_FISHING_TITLE_ID)
    .slice(0, QUERY_INPUT_SUGGESTION_LIMIT)
    .map((title, index) => queryInputSuggestion({
      kind: "title", label: title.name || String(title.titleId),
      description: siteText("map.search.title.description"),
      insertText: `title:${title.titleId}`, replacement,
      exactMatchInput: value, exactMatchValues: [title.name, String(title.titleId)],
      catalogKind: "title", catalogEntry: title, score: 270 - index,
    }));
}

function buildFishQueryInputSuggestions(stateBundle, replacement, rawValue) {
  const needle = normalizedSuggestionNeedle(rawValue);
  const fishSuggestions = indexedCatalogEntries(
    FISH_CATALOG_INDEX,
    stateBundle?.state?.catalog?.fish || [],
    needle,
    { suggestion: true },
  )
    .filter((fish) =>
      suggestionMatchesNeedle(needle, fishSearchValues(fish)),
    )
    .map((fish, index) => {
      const fishReferenceId = Number.parseInt(fish?.itemId ?? fish?.fishId, 10);
      if (!Number.isInteger(fishReferenceId) || fishReferenceId <= 0) {
        return null;
      }
      const exactMatchValues = [fish?.fishId, fish?.itemId, fish?.name];
      return queryInputSuggestion({
        kind: "fish",
        label: fish?.name || `Fish ${fishReferenceId || fish?.fishId}`,
        description: `fish:${fishReferenceId}`,
        insertText: `fish:${fishReferenceId}`,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues,
        catalogKind: "fish",
        catalogEntry: catalogEntryForSearchMatch({ kind: "fish", ...fish }),
        score: 270 - index,
      });
    });
  const itemSuggestions = indexedCatalogEntries(
    ITEM_SEARCH_CATALOG_INDEX,
    normalizeCatalogItems(stateBundle?.state?.catalog?.items || []),
    needle,
    { suggestion: true },
  )
    .filter((item) => suggestionMatchesNeedle(needle, itemSuggestionLabels(item)))
    .map((item, index) => {
      const itemIsFish = item?.isFish === true;
      const itemId = Number.parseInt(item?.itemId, 10);
      if (!Number.isInteger(itemId) || itemId <= 0) {
        return null;
      }
      const prefix = itemIsFish ? "fish" : "item";
      const exactMatchValues = itemSuggestionLabels(item);
      return queryInputSuggestion({
        kind: itemIsFish ? "fish" : "item",
        label: item?.name || `Item ${itemId}`,
        description: `${prefix}:${itemId}`,
        insertText: `${prefix}:${itemId}`,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues,
        catalogKind: "item",
        catalogEntry: catalogEntryForSearchMatch({ kind: "item", ...item }),
        score: 240 - index,
      });
    });
  return sortQueryInputSuggestions(
    buildSpecialItemQueryInputSuggestions(stateBundle, replacement, rawValue, 280)
      .concat(fishSuggestions, itemSuggestions),
  );
}

function itemSuggestionLabels(item) {
  return [
    item?.itemId,
    item?.name,
    ...(Array.isArray(item?.labels) ? item.labels : []),
  ];
}

function buildItemQueryInputSuggestions(stateBundle, replacement, rawValue) {
  const needle = normalizedSuggestionNeedle(rawValue);
  const itemSuggestions = indexedCatalogEntries(
    ITEM_SEARCH_CATALOG_INDEX,
    normalizeCatalogItems(stateBundle?.state?.catalog?.items || []),
    needle,
    { suggestion: true },
  )
    .filter((item) => suggestionMatchesNeedle(needle, itemSuggestionLabels(item)))
    .map((item, index) => {
      const itemIsFish = item?.isFish === true;
      const itemId = Number.parseInt(item?.itemId, 10);
      if (!Number.isInteger(itemId) || itemId <= 0) {
        return null;
      }
      const prefix = itemIsFish ? "fish" : "item";
      const exactMatchValues = itemSuggestionLabels(item);
      return queryInputSuggestion({
        kind: itemIsFish ? "fish" : "item",
        label: item?.name || `Item ${itemId}`,
        description: `${prefix}:${itemId}`,
        insertText: `${prefix}:${itemId}`,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues,
        catalogKind: "item",
        catalogEntry: catalogEntryForSearchMatch({ kind: "item", ...item }),
        score: 250 - index,
      });
    });
  const fishSuggestions = indexedCatalogEntries(
    FISH_CATALOG_INDEX,
    stateBundle?.state?.catalog?.fish || [],
    needle,
    { suggestion: true },
  )
    .filter((fish) =>
      suggestionMatchesNeedle(needle, fishSearchValues(fish)),
    )
    .map((fish, index) => {
      const fishReferenceId = Number.parseInt(fish?.itemId ?? fish?.fishId, 10);
      if (!Number.isInteger(fishReferenceId) || fishReferenceId <= 0) {
        return null;
      }
      const exactMatchValues = [fish?.fishId, fish?.itemId, fish?.name];
      return queryInputSuggestion({
        kind: "fish",
        label: fish?.name || `Fish ${fishReferenceId || fish?.fishId}`,
        description: `fish:${fishReferenceId}`,
        insertText: `fish:${fishReferenceId}`,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues,
        catalogKind: "fish",
        catalogEntry: catalogEntryForSearchMatch({ kind: "fish", ...fish }),
        score: 240 - index,
      });
    });
  return sortQueryInputSuggestions(
    buildSpecialItemQueryInputSuggestions(stateBundle, replacement, rawValue, 280)
      .concat(itemSuggestions, fishSuggestions),
  );
}

function buildTradeNpcQueryInputSuggestions(stateBundle, replacement, rawValue) {
  const needle = normalizedSuggestionNeedle(rawValue);
  const selectedNpcKeys = resolveSelectedTradeNpcKeys(stateBundle);
  const tradeNpcs = stateBundle?.state?.catalog?.tradeNpcs || [];
  return sortQueryInputSuggestions(
    indexedCatalogEntries(
      TRADE_NPC_CATALOG_INDEX,
      tradeNpcs,
      needle,
      { suggestion: true },
    )
      .filter((npc) =>
        !selectedNpcKeys.has(Number.parseInt(npc?.npcKey, 10)) &&
        suggestionMatchesNeedle(needle, tradeNpcSearchValues(npc)),
      )
      .map((npc, index) => {
        const insertText = tradeNpcQueryInputText(npc, tradeNpcs);
        if (!insertText) {
          return null;
        }
        return queryInputSuggestion({
          kind: "trade-npc",
          label: npc?.npcName || `NPC ${npc.npcKey}`,
          description: ["Trade NPC", npc?.assignedRegionLabel || ""]
            .filter(Boolean)
            .join(" · "),
          insertText,
          replacement,
          exactMatchInput: rawValue,
          exactMatchValues: tradeNpcSearchValues(npc),
          catalogKind: "trade-npc",
          catalogEntry: catalogEntryForSearchMatch({ kind: "trade-npc", ...npc }),
          score: 265 - index,
        });
      }),
  );
}

function buildStatusQueryInputSuggestions(replacement, rawValue) {
  const needle = normalizedSuggestionNeedle(rawValue);
  const metadata = fishFilterMetadata();
  return sortQueryInputSuggestions(
    ["favourite", "missing", "missing-titles"]
      .filter((term) =>
        suggestionMatchesNeedle(needle, fishFilterSearchValues(term, metadata[term])),
      )
      .map((term, index) =>
        queryInputSuggestion({
          kind: "status",
          label: metadata[term]?.label || term,
          description: "fish status",
          insertText: `status:${term}`,
          replacement,
          exactMatchInput: rawValue,
          exactMatchValues: [term, metadata[term]?.label],
          score: 230 - index,
        }),
      ),
  );
}

function buildGradeQueryInputSuggestions(replacement, rawValue) {
  const needle = normalizedSuggestionNeedle(rawValue);
  const metadata = fishFilterMetadata();
  return sortQueryInputSuggestions(
    ["red", "yellow", "blue", "green", "white"]
      .filter((term) =>
        suggestionMatchesNeedle(needle, fishFilterSearchValues(term, metadata[term])),
      )
      .map((term, index) =>
        queryInputSuggestion({
          kind: "grade",
          label: metadata[term]?.label || term,
          description: "fish grade",
          insertText: `grade:${term}`,
          replacement,
          exactMatchInput: rawValue,
          exactMatchValues: [term, metadata[term]?.label],
          score: 230 - index,
        }),
      ),
  );
}

function buildPatchQueryInputSuggestions(stateBundle, replacement, prefix, rawValue) {
  const needle = normalizedSuggestionNeedle(rawValue);
  return sortQueryInputSuggestions(
    indexedCatalogEntries(
      PATCH_CATALOG_INDEX,
      stateBundle?.state?.catalog?.patches || [],
      needle,
      { suggestion: true },
    )
      .filter((patch) =>
        suggestionMatchesNeedle(needle, patchCatalogIndexValues(patch)),
      )
      .map((patch, index) => {
        const patchId = normalizePatchId(patch?.patchId);
        return queryInputSuggestion({
          kind: "patch",
          label: patch?.label || patchId,
          description: `${prefix}:${patchId}`,
          insertText: `${prefix}:${patchId}`,
          replacement,
          exactMatchInput: rawValue,
          exactMatchValues: [patch?.patchId, patch?.label],
          catalogKind: "patch",
          catalogEntry: catalogEntryForSearchMatch({ kind: "patch-bound", patchId, label: patch?.label || patchId }),
          score: 230 - index,
        });
      }),
  );
}

function buildSemanticQueryInputSuggestions(stateBundle, replacement, rawValue) {
  const needle = normalizedSuggestionNeedle(rawValue);
  return sortQueryInputSuggestions(
    indexedCatalogEntries(
      SEMANTIC_CATALOG_INDEX,
      stateBundle?.state?.catalog?.semanticTerms || [],
      needle,
      { suggestion: true },
    )
      .filter((term) =>
        suggestionMatchesNeedle(needle, [
          term?.layerId,
          term?.layerName,
          term?.fieldId,
          term?.label,
          term?.description,
          term?.searchText,
        ]),
      )
      .map((term, index) =>
        queryInputSuggestion({
          kind: "semantic",
          label: term?.label || `Semantic ${term?.layerId}:${term?.fieldId}`,
          description: `${term?.layerName || term?.layerId || "semantic"} · semantic:${term?.layerId}:${term?.fieldId}`,
          insertText: `semantic:${term?.layerId}:${term?.fieldId}`,
          replacement,
          exactMatchInput: rawValue,
          exactMatchValues: [term?.layerId, term?.layerName, term?.fieldId, term?.label],
          catalogKind: "semantic",
          catalogEntry: catalogEntryForSearchMatch({ kind: "semantic", ...term }),
          score: 210 - index,
        }),
      ),
  );
}

function formatZoneSuggestionRgb(zoneRgb) {
  const value = Number.parseInt(zoneRgb, 10);
  return Number.isInteger(value) && value > 0
    ? `0x${value.toString(16).padStart(6, "0")}`
    : "";
}

function quotedQueryInputValue(value) {
  return `"${String(value ?? "").replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
}

function zoneSuggestionInsertText(zone, fallbackText) {
  const rawName = String(zone?.name || "").trim();
  if (rawName) {
    return `zone:${quotedQueryInputValue(rawName)}`;
  }
  return fallbackText ? `zone:${fallbackText}` : "";
}

function buildZoneQueryInputSuggestions(stateBundle, replacement, rawValue) {
  const zones = stateBundle?.state?.catalog?.zones || [];
  const zoneCandidates = String(rawValue ?? "").trim()
    ? indexedZoneCatalogEntries(zones, rawValue)
    : zones;
  const zoneMatches = String(rawValue ?? "").trim()
    ? findZoneMatches(zoneCandidates, rawValue)
    : zones.map((zone) => ({ ...zone, _score: 0 }));
  return sortQueryInputSuggestions(
    zoneMatches
      .map((zone, index) => {
        const zoneText = formatZoneSuggestionRgb(zone?.zoneRgb);
        if (!zoneText) {
          return null;
        }
        return queryInputSuggestion({
          kind: "zone",
          label: zone?.name || zoneText,
          description: "fishing zone",
          insertText: zoneSuggestionInsertText(zone, zoneText),
          replacement,
          exactMatchInput: rawValue,
          exactMatchValues: [
            zone?.name,
            zone?.rgbKey,
            zone?.rgbSpaced,
            zone?.hexKey,
            zone?.hashHexKey,
            zone?.bareHexKey,
            zoneText,
          ],
          catalogKind: "zone",
          catalogEntry: catalogEntryForSearchMatch({ kind: "zone", ...zone }),
          score: 260 - index + (Number.isFinite(zone?._score) ? Math.min(zone._score, 260) : 0),
        });
      }),
  );
}

function semanticTermMatchesLayer(term, layerIds) {
  const selected = new Set((Array.isArray(layerIds) ? layerIds : [layerIds])
    .map((layerId) => String(layerId ?? "").trim())
    .filter(Boolean));
  return selected.has(String(term?.layerId ?? "").trim());
}

function buildLayerSemanticQueryInputSuggestions(stateBundle, replacement, rawValue, {
  layerIds,
  kind,
  prefix,
  description,
}) {
  const needle = normalizedSuggestionNeedle(rawValue);
  return sortQueryInputSuggestions(
    indexedCatalogEntries(
      SEMANTIC_CATALOG_INDEX,
      stateBundle?.state?.catalog?.semanticTerms || [],
      needle,
      { suggestion: true },
    )
      .filter((term) =>
        semanticTermMatchesLayer(term, layerIds) &&
        suggestionMatchesNeedle(needle, [
          term?.fieldId,
          term?.label,
          term?.description,
          term?.searchText,
        ])
      )
      .map((term, index) =>
        queryInputSuggestion({
          kind,
          label: term?.label || `${description || prefix} ${term?.fieldId}`,
          description: `${description || term?.layerName || term?.layerId} · ${prefix}:${term?.fieldId}`,
          insertText: `${prefix}:${term?.fieldId}`,
          replacement,
          exactMatchInput: rawValue,
          exactMatchValues: [term?.fieldId, term?.label],
          catalogKind: "semantic",
          catalogEntry: catalogEntryForSearchMatch({ kind: "semantic", ...term }),
          score: 235 - index,
        }),
      ),
  );
}

function layerFilterSchema(layerId) {
  const normalized = normalizeLayerFilterId(layerId);
  return normalized ? QUERY_INPUT_LAYER_FILTER_SCHEMAS[normalized] || null : null;
}

function layerFilterExpressionPreview(layerId, bodyText) {
  const schema = layerFilterSchema(layerId);
  const normalizedLayerId = schema?.layerId || normalizeLayerFilterId(layerId);
  const body = String(bodyText || "").trim();
  return normalizedLayerId && body ? `filter:${normalizedLayerId}[${body}]` : "";
}

function buildLayerFilterLayerSuggestions(replacement, rawValue) {
  const needle = normalizedSuggestionNeedle(rawValue);
  return sortQueryInputSuggestions(
    Object.values(QUERY_INPUT_LAYER_FILTER_SCHEMAS)
      .filter((schema) => suggestionMatchesNeedle(needle, [
        schema.layerId,
        schema.label,
        ...(Array.isArray(schema.aliases) ? schema.aliases : []),
      ]))
      .map((schema, index) =>
        queryInputSuggestion({
          kind: "layer-filter-layer",
          label: schema.label,
          description: `filter:${schema.layerId}[...]`,
          insertText: `filter:${schema.layerId}[`,
          replacement,
          exactMatchInput: rawValue,
          exactMatchValues: [schema.layerId, schema.label, ...(schema.aliases || [])],
          score: 290 - index,
        }),
      ),
  );
}

function layerFactLayerIdForSuggestion(schema) {
  return schema?.layerId === "zones" ? "zone_mask" : schema?.layerId;
}

function layerFactSuggestionsForSchema(schema) {
  if (schema?.layerId === "zones") {
    return [
      {
        factId: "zone.loot.species",
        label: "Zone loot species",
        description: "species/item entries in zone loot tables",
      },
      {
        factId: "zone.loot.group",
        label: "Zone loot group",
        description: "group-level zone loot rates",
      },
    ];
  }
  return [];
}

function buildLayerFactQueryInputSuggestions(replacement, rawValue) {
  const raw = String(rawValue || "");
  const firstColon = raw.indexOf(":");
  if (firstColon < 0) {
    const needle = normalizedSuggestionNeedle(raw);
    return sortQueryInputSuggestions(
      Object.values(QUERY_INPUT_LAYER_FILTER_SCHEMAS)
        .map((schema) => ({ schema, layerId: layerFactLayerIdForSuggestion(schema) }))
        .filter(({ schema, layerId }) => suggestionMatchesNeedle(needle, [
          layerId,
          schema.label,
          ...(schema.aliases || []),
        ]))
        .map(({ schema, layerId }, index) =>
          queryInputSuggestion({
            kind: "layer-fact-layer",
            label: schema.label,
            description: `fact:${layerId}:...`,
            insertText: `fact:${layerId}:`,
            replacement,
            exactMatchInput: raw,
            exactMatchValues: [layerId, schema.label, ...(schema.aliases || [])],
            score: 250 - index,
          }),
        ),
    );
  }

  const rawLayerId = raw.slice(0, firstColon);
  const factNeedle = raw.slice(firstColon + 1);
  const normalizedLayerId = normalizeLayerFilterId(rawLayerId);
  const schema = layerFilterSchema(normalizedLayerId || rawLayerId);
  if (!schema) {
    return [];
  }
  const layerId = layerFactLayerIdForSuggestion(schema);
  const replacementWithPrefix = {
    ...replacement,
    start: replacement.start + "fact:".length + rawLayerId.length + 1,
    token: factNeedle,
  };
  return sortQueryInputSuggestions(
    layerFactSuggestionsForSchema(schema)
      .filter((fact) => suggestionMatchesNeedle(normalizedSuggestionNeedle(factNeedle), [
        fact.factId,
        fact.label,
        fact.description,
      ]))
      .map((fact, index) =>
        queryInputSuggestion({
          kind: "layer-fact",
          label: fact.label,
          description: fact.description,
          insertText: fact.factId,
          replacement: replacementWithPrefix,
          exactMatchInput: factNeedle,
          exactMatchValues: [fact.factId, fact.label],
          previewText: `fact:${layerId}:${fact.factId}`,
          score: 230 - index,
        }),
      ),
  );
}

function layerFilterBodyReplacement(ctx, replacement) {
  return {
    ...replacement,
    start: ctx.bodyStart + replacement.start,
    end: ctx.bodyStart + replacement.end,
  };
}

function quoteAwareBracketDepth(source, startIndex, endIndex) {
  let depth = 0;
  let quote = "";
  let escaped = false;
  for (let index = startIndex; index < endIndex; index += 1) {
    const char = source[index];
    if (quote) {
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === quote) {
        quote = "";
      }
      continue;
    }
    if (char === "\"" || char === "'") {
      quote = char;
    } else if (char === "[") {
      depth += 1;
    } else if (char === "]") {
      depth -= 1;
    }
  }
  return depth;
}

function activeLayerFilterContext(draftText, caretIndex) {
  const source = normalizeDraftText(draftText);
  const caret = normalizeCaretIndex(caretIndex, source);
  const beforeCaret = source.slice(0, caret);
  const pattern = /(^|[\s(])filter:([A-Za-z0-9_.-]+)\[/gi;
  let match;
  let selected = null;
  while ((match = pattern.exec(beforeCaret)) !== null) {
    const bracketStart = match.index + match[0].length - 1;
    if (bracketStart >= caret) {
      continue;
    }
    if (quoteAwareBracketDepth(source, bracketStart, caret) <= 0) {
      continue;
    }
    selected = {
      layerId: normalizeLayerFilterId(match[2]),
      layerRaw: match[2],
      bodyStart: bracketStart + 1,
      caret,
    };
  }
  if (!selected?.layerId) {
    return null;
  }
  return {
    ...selected,
    bodyText: source.slice(selected.bodyStart, caret),
  };
}

function buildLayerFilterSubjectPrefixSuggestions(schema, replacement, rawValue = "") {
  const needle = normalizedSuggestionNeedle(rawValue);
  return (schema?.subjects || [])
    .filter((subject) => suggestionMatchesNeedle(needle, [
      subject.prefix,
      subject.label,
      subject.insertText,
    ]))
    .map((subject, index) =>
      queryInputSuggestion({
        kind: "layer-filter-subject",
        label: subject.label,
        description: subject.insertText,
        insertText: subject.insertText,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues: [subject.prefix, subject.label],
        score: (subject.score ?? 220) - index,
      }),
    );
}

function buildLayerFilterGroupSuggestions(schema, replacement, rawValue = "", previewPrefix = "") {
  const needle = normalizedSuggestionNeedle(rawValue);
  return (schema?.groups || [])
    .filter((group) => suggestionMatchesNeedle(needle, [
      group.id,
      group.label,
    ]))
    .map((group, index) =>
      queryInputSuggestion({
        kind: "layer-filter-group",
        label: group.label,
        description: "loot group",
        insertText: group.id,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues: [group.id, group.label],
        previewText: previewPrefix ? layerFilterExpressionPreview(schema.layerId, `${previewPrefix}${group.id}`) : "",
        score: 230 - index,
      }),
    );
}

function buildLayerFilterRateSuggestions(schema, replacement, rawValue = "", previewPrefix = "") {
  const needle = normalizedSuggestionNeedle(rawValue);
  return (schema?.rateSuggestions || ["= 30%", "<= 50%", ">= 50%"])
    .filter((value) => suggestionMatchesNeedle(needle, [value]))
    .map((value, index) =>
      queryInputSuggestion({
        kind: "layer-filter-rate",
        label: value,
        description: "drop rate comparison",
        insertText: value,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues: [value],
        previewText: previewPrefix ? layerFilterExpressionPreview(schema.layerId, `${previewPrefix}${value}`) : "",
        score: 220 - index,
      }),
    );
}

function buildLayerFilterOperatorSuggestions(replacement, rawValue = "") {
  const needle = normalizedSuggestionNeedle(rawValue);
  return ["AND", "OR"]
    .filter((operator) => suggestionMatchesNeedle(needle, [operator.toLowerCase(), operator]))
    .map((operator, index) =>
      queryInputSuggestion({
        kind: "operator",
        label: operator,
        description: operator === "AND" ? "match all" : "match any",
        insertText: operator,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues: [operator, operator.toLowerCase()],
        score: 170 - index,
      }),
    );
}

function buildLayerFilterFieldSuggestions(schema, replacement, rawValue = "") {
  const needle = normalizedSuggestionNeedle(rawValue);
  return (schema?.fields || [])
    .filter((field) => suggestionMatchesNeedle(needle, [
      field.id,
      field.label,
      field.example,
    ]))
    .map((field, index) =>
      queryInputSuggestion({
        kind: "layer-filter-field",
        label: field.label,
        description: field.example || field.id,
        insertText: field.example || field.id,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues: [field.id, field.label],
        previewText: field.example ? layerFilterExpressionPreview(schema.layerId, field.example) : "",
        score: 235 - index,
      }),
    );
}

function buildLayerFilterRelationSuggestions(schema, replacement, rawValue = "") {
  const needle = normalizedSuggestionNeedle(rawValue);
  return (schema?.relations || [])
    .filter((relation) => suggestionMatchesNeedle(needle, [
      relation.id,
      relation.label,
      relation.insertText,
    ]))
    .map((relation, index) =>
      queryInputSuggestion({
        kind: "layer-filter-relation",
        label: relation.label,
        description: relation.insertText,
        insertText: relation.insertText,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues: [relation.id, relation.label],
        score: 232 - index,
      }),
    );
}

function buildLayerFilterSnippetSuggestions(schema, replacement, rawValue = "") {
  const needle = normalizedSuggestionNeedle(rawValue);
  return (schema?.snippets || [])
    .filter((snippet) => suggestionMatchesNeedle(needle, [
      snippet.label,
      snippet.description,
      snippet.insertText,
      ...(Array.isArray(snippet.aliases) ? snippet.aliases : []),
    ]))
    .map((snippet, index) =>
      queryInputSuggestion({
        ...snippet,
        replacement,
        exactMatchInput: rawValue,
        exactMatchValues: [snippet.label, snippet.insertText],
        previewText: layerFilterExpressionPreview(schema.layerId, snippet.insertText),
        score: (snippet.score ?? 210) - index,
      }),
    );
}

function layerFilterBodyPrefixBeforeToken(bodyText, replacement) {
  return String(bodyText || "").slice(0, replacement.start);
}

function buildLayerFilterContextualSuggestions(stateBundle, ctx, replacement) {
  const schema = layerFilterSchema(ctx.layerId);
  if (!schema) {
    return [];
  }
  const bodyBeforeToken = layerFilterBodyPrefixBeforeToken(ctx.bodyText, replacement);
  const bodyBeforeCaret = ctx.bodyText;
  const token = String(replacement.token || "");
  const lowerToken = token.toLowerCase();
  const globalReplacement = layerFilterBodyReplacement(ctx, replacement);
  const prefixed = lowerToken.match(/^([a-z_-]+):(.*)$/);
  if (prefixed) {
    const prefix = prefixed[1].replace(/-/g, "_");
    const value = token.slice(token.indexOf(":") + 1);
    switch (prefix) {
      case "fish":
        return buildFishQueryInputSuggestions(stateBundle, globalReplacement, value);
      case "item":
        return buildItemQueryInputSuggestions(stateBundle, globalReplacement, value);
      case "zone":
        return buildZoneQueryInputSuggestions(stateBundle, globalReplacement, value);
      case "group":
        return buildLayerFilterGroupSuggestions(schema, globalReplacement, value, "group:");
      default:
        break;
    }
  }

  const before = bodyBeforeCaret.trimEnd();
  const membershipGroupMatch = before.match(/^(.*\b(?:fish|item|zone):.+?\s+(?:NOT\s+)?IN\s+)([A-Za-z0-9_-]*)$/i);
  if (membershipGroupMatch && token && replacement.start >= membershipGroupMatch[1].length) {
    return sortQueryInputSuggestions(
      buildLayerFilterGroupSuggestions(
        schema,
        globalReplacement,
        token,
        membershipGroupMatch[1],
      ),
    );
  }

  const comparePrefixMatch = bodyBeforeToken.match(/^(.*(?:\b(?:fish|item|zone):.+?\s+IN\s+[A-Za-z0-9_-]+|group:[A-Za-z0-9_-]+)\s*)$/i);
  if (comparePrefixMatch && /^[=!<>~]*\s*\d*\.?\d*$/.test(token)) {
    return sortQueryInputSuggestions(
      buildLayerFilterRateSuggestions(schema, globalReplacement, token, comparePrefixMatch[1]),
    );
  }

  const subjectTailMatch = before.match(/\b(?:fish|item|zone):(?:"[^"]+"|'[^']+'|[^\[\]\n()]+)$/i);
  if (subjectTailMatch && !/\b(?:IN|NOT\s+IN)\b/i.test(subjectTailMatch[0])) {
    const insertReplacement = { ...globalReplacement, start: ctx.caret, end: ctx.caret, token: "" };
    return sortQueryInputSuggestions(
      buildLayerFilterGroupSuggestions(schema, insertReplacement, "", "IN ")
        .map((suggestion) => ({
          ...suggestion,
          insertText: `IN ${suggestion.insertText}`,
          previewText: "",
        }))
        .concat(buildLayerFilterOperatorSuggestions(insertReplacement)),
    );
  }

  const completeMembershipMatch = before.match(/\b(?:fish|item|zone):.+?\s+(IN|NOT\s+IN)\s+[A-Za-z0-9_-]+\s*$/i);
  if (completeMembershipMatch) {
    const insertReplacement = { ...globalReplacement, start: ctx.caret, end: ctx.caret, token: "" };
    const membershipOp = completeMembershipMatch[1].toUpperCase().replace(/\s+/g, " ");
    const rateSuggestions = membershipOp === "IN"
      ? buildLayerFilterRateSuggestions(schema, insertReplacement, "", `${before} `)
      : [];
    return sortQueryInputSuggestions(
      rateSuggestions.concat(buildLayerFilterOperatorSuggestions(insertReplacement)),
    );
  }

  return sortQueryInputSuggestions(
    buildLayerFilterSubjectPrefixSuggestions(schema, globalReplacement, token)
      .concat(buildLayerFilterFieldSuggestions(schema, globalReplacement, token))
      .concat(buildLayerFilterRelationSuggestions(schema, globalReplacement, token))
      .concat(buildLayerFilterSnippetSuggestions(schema, globalReplacement, token))
      .concat(buildLayerFilterOperatorSuggestions(globalReplacement, token)),
  );
}

function buildLayerFilterQueryInputSuggestions(stateBundle, ctx) {
  const spacedReplacement = draftSpacedValueReplacementSpan(ctx.bodyText, ctx.bodyText.length);
  if (spacedReplacement) {
    const spacedSuggestions = buildLayerFilterContextualSuggestions(stateBundle, ctx, spacedReplacement);
    const allowedSuggestions = spacedValueContinuationSuggestions(spacedReplacement, spacedSuggestions);
    if (allowedSuggestions.length) {
      return allowedSuggestions;
    }
  }
  return buildLayerFilterContextualSuggestions(
    stateBundle,
    ctx,
    draftTokenReplacementSpan(ctx.bodyText, ctx.bodyText.length),
  );
}

function buildSourceKindQueryInputSuggestions(replacement, token) {
  const atIndex = token.lastIndexOf("@");
  const sourceScope = atIndex >= 0 ? token.slice(atIndex + 1) : "";
  const lastCommaIndex = sourceScope.lastIndexOf(",");
  const sourcePrefix = lastCommaIndex >= 0 ? sourceScope.slice(0, lastCommaIndex + 1) : "";
  const base = atIndex >= 0 ? `${token.slice(0, atIndex + 1)}${sourcePrefix}` : token;
  const rawNeedle = lastCommaIndex >= 0 ? sourceScope.slice(lastCommaIndex + 1) : sourceScope;
  const needle = normalizedSuggestionNeedle(rawNeedle);
  return sortQueryInputSuggestions(
    QUERY_INPUT_SOURCE_KIND_SUGGESTIONS
      .filter((candidate) =>
        suggestionMatchesNeedle(needle, [
          candidate.sourceKind,
          candidate.label,
          ...candidate.aliases,
        ]),
      )
      .map((candidate, index) => {
        const sourceKind = normalizeSourceKind(candidate.sourceKind);
        return queryInputSuggestion({
          kind: "source-kind",
          label: candidate.label || sourceKind,
          description: candidate.description,
          insertText: `${base}${sourceKind}`,
          replacement,
          score: 250 - index,
        });
      }),
  );
}

function replacementPrefixedRawValue(replacement) {
  const token = String(replacement?.token || "");
  const colonIndex = token.indexOf(":");
  return colonIndex >= 0 ? token.slice(colonIndex + 1) : token;
}

function spacedValueContinuationSuggestions(replacement, suggestions) {
  if (!Array.isArray(suggestions) || suggestions.length <= 1) {
    return suggestions || [];
  }
  const rawValue = replacementPrefixedRawValue(replacement);
  const exactMatches = suggestions.filter((suggestion) =>
    queryInputSuggestionExactMatchesValue(rawValue, suggestion?.exactMatchValues || []),
  );
  return exactMatches.length === 1 ? exactMatches : [];
}

export function buildQueryInputSuggestions(stateBundle, draftText, caretIndex) {
  const layerFilterContext = activeLayerFilterContext(draftText, caretIndex);
  if (layerFilterContext) {
    return buildLayerFilterQueryInputSuggestions(stateBundle, layerFilterContext);
  }
  const tokenReplacement = draftTokenReplacementSpan(draftText, caretIndex);
  const operator = booleanOperatorSuggestion(tokenReplacement);
  if (operator) {
    return [operator];
  }
  const spacedReplacement = draftSpacedValueReplacementSpan(draftText, caretIndex);
  if (spacedReplacement) {
    const spacedSuggestions = buildQueryInputSuggestionsForReplacement(stateBundle, spacedReplacement);
    const allowedSuggestions = spacedValueContinuationSuggestions(spacedReplacement, spacedSuggestions);
    if (allowedSuggestions.length) {
      return allowedSuggestions;
    }
    if (spacedSuggestions.length) {
      return spacedSuggestions;
    }
  }
  const plainSearchReplacement = draftPlainSearchReplacementSpan(stateBundle, draftText, caretIndex);
  if (plainSearchReplacement) {
    return buildQueryInputSuggestionsForReplacement(stateBundle, plainSearchReplacement);
  }
  return buildQueryInputSuggestionsForReplacement(
    stateBundle,
    tokenReplacement,
  );
}

function buildQueryInputSuggestionsForReplacement(stateBundle, replacement) {
  const suggestions = buildQueryInputScopeSuggestions(stateBundle, replacement);
  const prefixed = replacement.token.match(/^([a-z_-]+):/i);
  if (prefixed && !["fish", "item"].includes(prefixed[1].toLowerCase())) return suggestions;
  const rawValue = replacementPrefixedRawValue(replacement);
  const tokens = tokenizeSearchExpressionDsl(rawValue);
  const value = tokens.length === 1 && tokens[0].type === "value" ? tokens[0].value : rawValue;
  const group = resolveSearchExpressionItemNameGroup(value, { catalog: stateBundle.state.catalog });
  if (!group) return suggestions;
  const insertText = `fish:${JSON.stringify(group.name)}`;
  return [queryInputSuggestion({
    kind: "item-group", label: mapText("search.item_group.any", { label: group.name }),
    insertText, previewText: insertText, replacement,
    exactMatchInput: value, exactMatchValues: [group.name],
    catalogKind: "item-group", catalogEntry: { kind: "item-group", itemIds: group.itemIds },
    score: 4000,
  }), ...suggestions].slice(0, QUERY_INPUT_SUGGESTION_LIMIT);
}

function buildQueryInputScopeSuggestions(stateBundle, replacement) {
  const token = replacement.token;
  const lowerToken = token.toLowerCase();
  if (/^(?:item|fish):[^@\s()]*@/i.test(token)) {
    return buildSourceKindQueryInputSuggestions(replacement, token);
  }
  const prefixed = lowerToken.match(/^([a-z_-]+):(.*)$/);
  if (prefixed) {
    const prefix = prefixed[1].replace(/-/g, "_");
    const value = token.slice(token.indexOf(":") + 1);
    switch (prefix) {
      case "title":
        return buildTitleQueryInputSuggestions(stateBundle, replacement, value);
      case "fish":
        return buildFishQueryInputSuggestions(stateBundle, replacement, value);
      case "item":
        return buildItemQueryInputSuggestions(stateBundle, replacement, value);
      case "npc":
        return buildTradeNpcQueryInputSuggestions(stateBundle, replacement, value);
      case "status":
        return buildStatusQueryInputSuggestions(replacement, value);
      case "grade":
        return buildGradeQueryInputSuggestions(replacement, value);
      case "patch":
      case "from":
      case "to":
      case "after":
      case "before":
        return buildPatchQueryInputSuggestions(stateBundle, replacement, prefix, value);
      case "zone":
        return buildZoneQueryInputSuggestions(stateBundle, replacement, value);
      case "region":
      case "regions":
        return buildLayerSemanticQueryInputSuggestions(stateBundle, replacement, value, {
          layerIds: ["regions"],
          kind: "region",
          prefix: "region",
          description: "region",
        });
      case "regiongroup":
      case "regiongroups":
      case "region_group":
      case "region_groups":
        return buildLayerSemanticQueryInputSuggestions(stateBundle, replacement, value, {
          layerIds: ["region_groups"],
          kind: "regiongroup",
          prefix: "regiongroup",
          description: "region group",
        });
      case "semantic":
        return buildSemanticQueryInputSuggestions(stateBundle, replacement, value);
      case "filter":
        return buildLayerFilterLayerSuggestions(replacement, value);
      case "fact":
        return buildLayerFactQueryInputSuggestions(replacement, value);
      default:
        return [];
    }
  }
  return buildGeneralQueryInputSuggestions(stateBundle, replacement);
}

export function applyQueryInputSuggestionToDraft(draftText, suggestion) {
  const source = normalizeDraftText(draftText);
  const start = Math.max(0, Math.min(source.length, Number.parseInt(suggestion?.replaceStart, 10)));
  const end = Math.max(start, Math.min(source.length, Number.parseInt(suggestion?.replaceEnd, 10)));
  const insertText = String(suggestion?.insertText ?? "");
  const before = source.slice(0, start);
  const after = source.slice(end);
  const needsTrailingSpace = insertText && !insertText.endsWith(" ") && !/^\s|^\)/.test(after);
  const nextText = `${before}${insertText}${needsTrailingSpace ? " " : ""}${after}`;
  return {
    draftText: nextText,
    caretIndex: before.length + insertText.length + (needsTrailingSpace ? 1 : 0),
  };
}

function validQueryInputLeadingPrefix(stateBundle, source, leadingExpressionEnd) {
  if (!Number.isInteger(leadingExpressionEnd) || leadingExpressionEnd <= 0) {
    return null;
  }
  const catalog = queryInputCatalogFromBundle(stateBundle);
  const sourceText = source.slice(0, leadingExpressionEnd).trimEnd();
  if (!sourceText.trim()) {
    return null;
  }
  const parsed = parseSearchExpressionDslResult(sourceText, {
    catalog,
    includeRawExpressionTree: true,
  });
  const issues = Array.isArray(parsed.issues)
    ? parsed.issues.filter((issue) => issue && typeof issue === "object")
    : [];
  if (!parsed.expression || issues.some((issue) => issue.severity !== "warning")) {
    return null;
  }
  return {
    sourceText,
    sourceRange: { start: 0, end: sourceText.length },
    expression: parsed.expression,
    rawExpressionTree: normalizeRawSearchExpressionTree(parsed.rawExpressionTree),
    issues: cloneJson(issues),
    canonicalDsl: serializeSearchExpressionDsl(parsed.expression),
  };
}

export function buildQueryInputDraftResultFromBundle(stateBundle, draftText) {
  const source = normalizeDraftText(draftText);
  const result = parseSearchExpressionDslResult(source, {
    catalog: queryInputCatalogFromBundle(stateBundle),
    includeRawExpressionTree: true,
  });
  const issues = Array.isArray(result.issues)
    ? result.issues.filter((issue) => issue && typeof issue === "object")
    : [];
  const expression = result.expression;
  if (expressionUsesTitles(expression) && stateBundle.titleProjection?.kind !== "ready") {
    issues.push({ stage: "resolve", severity: "error", code: "title_catalog_unavailable",
      message: siteText(stateBundle.titleProjection?.kind === "error" ? "fishydex.titles.catalog_error" : "fishydex.titles.loading") });
  }
  const hasErrors = issues.some((issue) => issue.severity !== "warning");
  const canApply = !hasErrors;
  return {
    draftText: source,
    expression,
    rawExpressionTree: normalizeRawSearchExpressionTree(result.rawExpressionTree),
    issues: cloneJson(issues),
    canApply,
    canonicalDsl: canApply && expression ? serializeSearchExpressionDsl(expression) : "",
    validPrefix: canApply
      ? null
      : validQueryInputLeadingPrefix(stateBundle, source, result.leadingExpressionEnd),
  };
}

export function resolveSelectedZoneRgbs(stateBundle) {
  return normalizeIntegerList(
    resolveSelectedSearchTermsFromBundle(stateBundle)
      .filter((term) => term?.kind === "zone")
      .map((term) => term.zoneRgb),
  );
}

export function resolveSelectedFishFilterTerms(stateBundle) {
  return normalizeFishFilterTerms(
    resolveSelectedSearchTermsFromBundle(stateBundle)
      .flatMap((term) => {
        if (term?.kind === "status") {
          return [normalizeSearchStatusTerm(term.status)];
        }
        if (term?.kind === "grade") {
          return [normalizeSearchGradeTerm(term.grade)];
        }
        return [];
      }),
  );
}

function resolveSelectedPatchTerms(stateBundle) {
  const selectedTerms = resolveSelectedSearchTermsFromBundle(stateBundle);
  return selectedTerms.filter((term) => term?.kind === "patch-bound");
}

function resolveSelectedPatchBounds(stateBundle) {
  const patchTerms = resolveSelectedPatchTerms(stateBundle);
  let fromPatchId = null;
  let toPatchId = null;
  for (const term of patchTerms) {
    const patchId = normalizePatchId(term.patchId);
    if (!patchId) {
      continue;
    }
    if (term.bound === "from") {
      fromPatchId = patchId;
      continue;
    }
    if (term.bound === "to") {
      toPatchId = patchId;
    }
  }
  return { fromPatchId, toPatchId };
}

function resolveSelectedPatchBoundSet(stateBundle) {
  return new Set(
    resolveSelectedPatchTerms(stateBundle)
      .map((term) => normalizePatchBound(term?.bound))
      .filter(Boolean),
  );
}

export function parseFishFilterDirectives(searchText) {
  const rawQuery = String(searchText || "").trim().toLowerCase().replace(/\s+/g, " ");
  if (!rawQuery) {
    return {
      rawQuery: "",
      remainingQuery: "",
      directTerms: [],
    };
  }

  let remainingQuery = rawQuery;
  const directTerms = new Set();
  const replacements = [
    {
      term: "missing",
      patterns: [/\bnot\s+yet\s+caught\b/g, /\bnot\s+caught\b/g, /\buncaught\b/g, /\bmissing\b(?!-titles)/g],
    },
    {
      term: "favourite",
      patterns: [/\bfavou?rites?\b/g],
    },
  ];
  for (const replacement of replacements) {
    for (const pattern of replacement.patterns) {
      remainingQuery = remainingQuery.replace(pattern, () => {
        directTerms.add(replacement.term);
        return " ";
      });
    }
  }
  remainingQuery = remainingQuery.replace(/\s+/g, " ").trim();
  return {
    rawQuery,
    remainingQuery,
    directTerms: FISH_FILTER_TERM_ORDER.filter((term) => directTerms.has(term)),
  };
}

function searchMatchLookupRank(values, searchText) {
  return matchQueryValues(values, searchText).rank;
}

function searchMatchLookup(values, searchText) {
  return matchQueryValues(values, searchText);
}

function searchMatchRank(match) {
  const rank = Number.parseInt(match?._matchRank, 10);
  return Number.isInteger(rank) ? rank : QUERY_MATCH_RANK_NONE;
}

function compareSearchMatches(left, right) {
  const leftRank = searchMatchRank(left);
  const rightRank = searchMatchRank(right);
  if (rightRank !== leftRank) {
    return rightRank - leftRank;
  }
  if (right._score !== left._score) {
    return right._score - left._score;
  }
  const leftPriority = searchMatchPriority(left);
  const rightPriority = searchMatchPriority(right);
  if (leftPriority !== rightPriority) {
    return leftPriority - rightPriority;
  }
  return String(left.name || left.label || "").localeCompare(
    String(right.name || right.label || ""),
  );
}

function addSearchMatch(matches, match, options = {}) {
  if (!match || options.excludeMatch?.(match) === true) {
    return;
  }
  const limit = Number.parseInt(options.limit, 10);
  const key = options.dedupeKey?.(match);
  if (key) {
    const duplicateIndex = matches.findIndex((candidate) => options.dedupeKey(candidate) === key);
    if (duplicateIndex >= 0) {
      const compareDuplicate = options.compareDuplicate || compareSearchMatches;
      if (compareDuplicate(match, matches[duplicateIndex]) >= 0) {
        return;
      }
      matches.splice(duplicateIndex, 1);
    }
  }
  matches.push(match);
  if (!Number.isInteger(limit) || limit <= 0) {
    return;
  }
  if (matches.length > limit) {
    let worstIndex = 0;
    for (let index = 1; index < matches.length; index += 1) {
      if (compareSearchMatches(matches[index], matches[worstIndex]) > 0) {
        worstIndex = index;
      }
    }
    matches.splice(worstIndex, 1);
  }
}

function finishSearchMatches(matches, options, compareUnbounded) {
  if (Number.parseInt(options?.limit, 10) > 0) {
    return matches.sort(compareSearchMatches);
  }
  return matches.sort(compareUnbounded);
}

function fishFilterMatchRank(term, searchText, metadata, queryMatchState = searchText) {
  const query = normalizeQueryMatchText(searchText);
  if (
    query.length >= 3 &&
    String(term || "").toLowerCase().startsWith(query)
  ) {
    return QUERY_MATCH_RANK_EXACT;
  }
  return searchMatchLookupRank(fishFilterSearchValues(term, metadata), queryMatchState);
}

function fishSearchValues(fish) {
  if (!fish || typeof fish !== "object") {
    return [];
  }
  const cached = FISH_SEARCH_VALUES_CACHE.get(fish);
  if (cached) {
    return cached;
  }
  const values = [
    fish?.fishId,
    fish?.itemId,
    fish?.name,
  ];
  FISH_SEARCH_VALUES_CACHE.set(fish, values);
  return values;
}

function itemSearchValues(item) {
  if (!item || typeof item !== "object") {
    return [];
  }
  const cached = ITEM_SEARCH_VALUES_CACHE.get(item);
  if (cached) {
    return cached;
  }
  const values = [
    item?.itemId,
    item?.name,
    itemLabelValues(item),
  ];
  ITEM_SEARCH_VALUES_CACHE.set(item, values);
  return values;
}

function semanticSearchValues(term) {
  if (!term || typeof term !== "object") {
    return [];
  }
  const cached = SEMANTIC_SEARCH_VALUES_CACHE.get(term);
  if (cached) {
    return cached;
  }
  const values = [
    term.fieldId,
    term.label,
    term.description,
    term.layerName,
    term.searchText,
  ];
  SEMANTIC_SEARCH_VALUES_CACHE.set(term, values);
  return values;
}

function tradeNpcSearchValues(npc) {
  if (!npc || typeof npc !== "object") {
    return [];
  }
  const cached = TRADE_NPC_SEARCH_VALUES_CACHE.get(npc);
  if (cached) {
    return cached;
  }
  const values = [
    npc.npcKey,
    npc.npcName,
    npc.class,
    npc.classLabel,
    npc.assignedRegionLabel,
    npc.sellOriginLabel,
    ...(Array.isArray(npc.sourceTags) ? npc.sourceTags : []),
    "trade npc",
    "trade manager",
    "trader",
    "imperial fishing delivery",
  ];
  TRADE_NPC_SEARCH_VALUES_CACHE.set(npc, values);
  return values;
}

function itemLabelValues(item) {
  const labels = [];
  const pushLabel = (value) => {
    const label = String(value ?? "").trim();
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  };
  pushLabel(item?.name);
  for (const value of Array.isArray(item?.labels) ? item.labels : []) {
    pushLabel(value);
  }
  return labels;
}

function normalizeCatalogItem(item) {
  const itemId = Number.parseInt(item?.itemId, 10);
  if (!Number.isInteger(itemId) || itemId <= 0) {
    return null;
  }
  const name = String(item?.name || `Item ${itemId}`).trim();
  const labels = itemLabelValues({ ...item, name });
  return {
    ...item,
    itemId,
    name,
    labels,
    sourceKinds: normalizeSourceKinds(item?.sourceKinds),
  };
}

function itemFallbackName(itemId) {
  return `Item ${itemId}`;
}

function isFallbackItemName(value, itemId) {
  return String(value || "").trim() === itemFallbackName(itemId);
}

function mergeCatalogItemsForSameId(existing, item) {
  const itemId = existing.itemId;
  const name = isFallbackItemName(existing.name, itemId) && !isFallbackItemName(item.name, itemId)
    ? item.name
    : existing.name;
  return {
    ...existing,
    name,
    label: existing.label || item.label,
    labels: normalizeStringList([...(existing.labels || []), ...(item.labels || [])]),
    sourceKinds: normalizeSourceKinds([...(existing.sourceKinds || []), ...(item.sourceKinds || [])]),
    grade: existing.grade || item.grade,
    isPrize: existing.isPrize === true || item.isPrize === true,
    isFish: existing.isFish === true || item.isFish === true,
    iconItemId: existing.iconItemId ?? item.iconItemId,
    iconImage: existing.iconImage || item.iconImage,
  };
}

function normalizeCatalogItems(catalogItems) {
  const items = Array.isArray(catalogItems) ? catalogItems : EMPTY_QUERY_INPUT_ARRAY;
  const cached = NORMALIZED_ITEM_CATALOG_CACHE.get(items);
  if (cached?.length === items.length) {
    return cached.value;
  }
  const byItemId = new Map();
  for (const item of items) {
    const normalized = normalizeCatalogItem(item);
    if (!normalized) {
      continue;
    }
    const existing = byItemId.get(normalized.itemId);
    byItemId.set(
      normalized.itemId,
      existing ? mergeCatalogItemsForSameId(existing, normalized) : normalized,
    );
  }
  const value = Array.from(byItemId.values()).sort((left, right) => left.itemId - right.itemId);
  NORMALIZED_ITEM_CATALOG_CACHE.set(items, { length: items.length, value });
  return value;
}

function itemSourceKindOrder(sourceKind) {
  return ITEM_SOURCE_KIND_RESULT_ORDER.get(String(sourceKind || "").trim().toLowerCase())
    ?? Number.MAX_SAFE_INTEGER;
}

function sourceKindsForResultRows(sourceKinds) {
  return normalizeSourceKinds(sourceKinds).sort((left, right) => {
    const order = itemSourceKindOrder(left) - itemSourceKindOrder(right);
    return order || left.localeCompare(right);
  });
}

function itemResultSourceScopes(sourceKinds) {
  const normalized = sourceKindsForResultRows(sourceKinds);
  if (normalized.length <= 1) {
    return [normalized];
  }
  return [normalized, ...normalized.map((sourceKind) => [sourceKind])];
}

function sourceScopeSortWeight(sourceKinds) {
  const normalized = sourceKindsForResultRows(sourceKinds);
  if (normalized.length > 1) {
    return -1;
  }
  return itemSourceKindOrder(normalized[0]);
}

function sourceScopedItem(item, sourceKinds) {
  return {
    ...item,
    sourceKinds: sourceKindsForResultRows(sourceKinds),
  };
}

function itemMatchSearchTerm(match) {
  if (match?.kind === "item-group") {
    const sourceKinds = normalizeSourceKinds(match.sourceKinds);
    return sourceKinds.length
      ? { kind: "item-group", itemIds: match.itemIds, sourceKinds }
      : { kind: "item-group", itemIds: match.itemIds };
  }
  if (match?.kind === "item") {
    const sourceKinds = normalizeSourceKinds(match.sourceKinds);
    return sourceKinds.length
      ? { kind: "item", itemId: match.itemId, sourceKinds }
      : { kind: "item", itemId: match.itemId };
  }
  return null;
}

function unscopedItemMatchSearchTerm(match) {
  if (match?.kind === "item-group") {
    return { kind: "item-group", itemIds: match.itemIds };
  }
  if (match?.kind === "item") {
    return { kind: "item", itemId: match.itemId };
  }
  return null;
}

function selectedItemTermKeys(stateBundle) {
  return new Set(
    resolveSelectedSearchTermsFromBundle(stateBundle)
      .filter((term) => term?.kind === "item" || term?.kind === "item-group")
      .map(searchTermKey)
      .filter(Boolean),
  );
}

function itemMatchIsSelected(match, selectedKeys) {
  const scopedKey = searchTermKey(itemMatchSearchTerm(match));
  if (scopedKey && selectedKeys.has(scopedKey)) {
    return true;
  }
  const unscopedKey = searchTermKey(unscopedItemMatchSearchTerm(match));
  return Boolean(unscopedKey && selectedKeys.has(unscopedKey));
}

function resolveFishGradeFilterTerm(fish) {
  const grade = String(fish?.grade ?? "").trim().toLowerCase();
  if (fish?.isPrize === true || grade === "prize" || grade === "red") {
    return "red";
  }
  if (grade === "rare" || grade === "yellow") {
    return "yellow";
  }
  if (
    grade === "highquality" ||
    grade === "high_quality" ||
    grade === "high-quality" ||
    grade === "blue"
  ) {
    return "blue";
  }
  if (grade === "general" || grade === "green") {
    return "green";
  }
  if (grade === "trash" || grade === "white") {
    return "white";
  }
  return "";
}

function fishEntryIsDried(fish) {
  return fish?.isDried === true;
}

function fishEntryDerivedFromItemId(fish) {
  const id = Number.parseInt(fish?.derivedFromItemId, 10);
  return Number.isInteger(id) && id > 0 ? id : 0;
}

function resolveFishIdentityIds(fish, options = {}) {
  const ids = [];
  const seen = new Set();
  const lineageId =
    options.includeDriedLineage === true && fishEntryIsDried(fish)
      ? fishEntryDerivedFromItemId(fish)
      : 0;
  for (const value of [fish?.fishId, fish?.itemId, lineageId]) {
    const id = Number.parseInt(value, 10);
    if (!Number.isInteger(id) || id <= 0 || seen.has(id)) {
      continue;
    }
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

function fishMatchesFilterTerms(fish, filterTerms, sharedFishState) {
  if (fishEntryIsDried(fish) && sharedFishState?.showDried !== true) {
    return false;
  }
  if (!filterTerms.length) {
    return true;
  }
  const fishIdentityIds = resolveFishIdentityIds(fish, {
    includeDriedLineage: sharedFishState?.showDried === true,
  });
  if (!fishIdentityIds.length) {
    return false;
  }
  const selectedGradeTerms = filterTerms.filter((term) => FISH_GRADE_FILTER_TERMS.has(term));
  if (selectedGradeTerms.length) {
    const gradeTerm = resolveFishGradeFilterTerm(fish);
    if (!selectedGradeTerms.includes(gradeTerm)) {
      return false;
    }
  }
  for (const term of filterTerms) {
    if (FISH_GRADE_FILTER_TERMS.has(term)) {
      continue;
    }
    if (
      term === "favourite" &&
      !fishIdentityIds.some((fishId) => sharedFishState?.favouriteSet?.has(fishId))
    ) {
      return false;
    }
    if (
      term === "missing-titles" &&
      !fishIdentityIds.some(id => sharedFishState?.missingTitleSet?.has(id))
    ) return false;
    if (
      term === "missing" &&
      fishIdentityIds.some((fishId) => sharedFishState?.caughtSet?.has(fishId))
    ) {
      return false;
    }
  }
  return true;
}

function findFishMatches(catalogFish, searchText, options = {}) {
  const query = normalizeQueryMatchText(searchText);
  const terms = query ? query.split(/\s+/g).filter(Boolean) : [];
  const includeAllWhenEmpty = options.includeAllWhenEmpty === true;
  const filterTerms = normalizeFishFilterTerms(options.filterTerms);
  const sharedFishState = normalizeSharedFishState(options.sharedFishState);
  if (!terms.length && !includeAllWhenEmpty) {
    return [];
  }
  const filtered = [];
  const boundedOneCharacter =
    compactQueryMatchText(searchText).length === 1 &&
    Number.parseInt(options.limit, 10) > 0;
  const includeFish = (fish) =>
    !(fishEntryIsDried(fish) && sharedFishState.showDried !== true) &&
    fishMatchesFilterTerms(fish, filterTerms, sharedFishState) &&
    options.excludeMatch?.(fish) !== true;
  const catalogIndex = FISH_CATALOG_INDEX(catalogFish);
  const candidateRecords = terms.length
    ? boundedOneCharacter
      ? queryCatalogOneCharacterCandidates(
          catalogIndex,
          searchText,
          {
            limit: options.limit,
            prefixOnly: options.prefixOnly === true,
            includeRecord: (record) => includeFish(record.value),
          },
        )
      : options.prefixOnly === true
        ? queryCatalogPrefixCandidates(catalogIndex, searchText)
        : queryCatalogCandidates(catalogIndex, searchText)
    : catalogIndex.records;
  for (const record of candidateRecords) {
    const fish = record.value;
    if (fishEntryIsDried(fish) && sharedFishState.showDried !== true) {
      continue;
    }
    if (!fishMatchesFilterTerms(fish, filterTerms, sharedFishState)) {
      continue;
    }
    const match = terms.length
      ? queryCatalogRecordMatch(
          catalogIndex,
          record,
          options.queryMatchState || searchText,
        )
      : null;
    if (terms.length && (!match || match.rank <= QUERY_MATCH_RANK_NONE)) {
      continue;
    }
    addSearchMatch(filtered, {
      kind: "fish",
      ...fish,
      _score: match?.score || 0,
      _matchRank: match?.rank || QUERY_MATCH_RANK_NONE,
      _queryMatch: match || null,
    }, options);
  }
  return finishSearchMatches(filtered, options, (left, right) => {
    if (terms.length && right._score !== left._score) {
      return right._score - left._score;
    }
    return String(left.name || "").localeCompare(String(right.name || ""));
  });
}

function findItemMatches(catalogItems, searchText, options = {}) {
  const query = normalizeQueryMatchText(searchText);
  const terms = query ? query.split(/\s+/g).filter(Boolean) : [];
  if (!terms.length) {
    return [];
  }
  const filtered = [];
  const normalizedItems = normalizeCatalogItems(catalogItems);
  const boundedOneCharacter =
    compactQueryMatchText(searchText).length === 1 &&
    Number.parseInt(options.limit, 10) > 0;
  const includeItem = options.hasExclusions === true
    ? (item) => itemResultSourceScopes(item.sourceKinds).some((sourceKinds) =>
        options.excludeMatch?.({ kind: "item", ...sourceScopedItem(item, sourceKinds) }) !== true
      )
    : null;
  const itemCatalogIndex = ITEM_SEARCH_CATALOG_INDEX(normalizedItems);
  const itemCandidateRecords = boundedOneCharacter
    ? queryCatalogOneCharacterCandidates(
        itemCatalogIndex,
        searchText,
        {
          limit: options.limit,
          prefixOnly: options.prefixOnly === true,
          includeRecord: includeItem ? (record) => includeItem(record.value) : null,
        },
      )
    : options.prefixOnly === true
      ? queryCatalogPrefixCandidates(itemCatalogIndex, searchText)
      : queryCatalogCandidates(itemCatalogIndex, searchText);
  for (const record of itemCandidateRecords) {
    const item = record.value;
    const match = queryCatalogRecordMatch(
      itemCatalogIndex,
      record,
      options.queryMatchState || searchText,
    );
    if (match.rank <= QUERY_MATCH_RANK_NONE) {
      continue;
    }
    options.matchedIdentityIds?.add(item.itemId);
    if (options.prefixOnly === true && match.rank < QUERY_MATCH_RANK_PREFIX) {
      continue;
    }
    const sourceKinds = sourceKindsForResultRows(item.sourceKinds);
    for (const rowSourceKinds of itemResultSourceScopes(sourceKinds)) {
      addSearchMatch(filtered, {
        kind: "item",
        ...sourceScopedItem(item, rowSourceKinds),
        _score: match.score,
        _matchRank: match.rank,
        _queryMatch: match,
      }, options);
    }
  }
  return finishSearchMatches(filtered, options, (left, right) => {
    if (right._score !== left._score) {
      return right._score - left._score;
    }
    const nameOrder = String(left.name || "").localeCompare(String(right.name || ""));
    if (nameOrder) {
      return nameOrder;
    }
    const sourceOrder = sourceScopeSortWeight(left.sourceKinds) - sourceScopeSortWeight(right.sourceKinds);
    if (sourceOrder) {
      return sourceOrder;
    }
    return (left.itemId || 0) - (right.itemId || 0);
  });
}

function fishMatchIsCoveredByItems(fish, itemIds) {
  if (!(itemIds instanceof Set) || itemIds.size === 0) {
    return false;
  }
  return resolveFishIdentityIds(fish).some((id) => itemIds.has(id));
}

function fishMatchIsCoveredByCatalogItems(fish, catalogItems, queryMatchState) {
  const normalizedItems = normalizeCatalogItems(catalogItems);
  const itemById = buildQueryInputItemIdentityLookup(normalizedItems);
  for (const id of resolveFishIdentityIds(fish)) {
    const item = itemById.get(id);
    if (
      item &&
      searchMatchLookup(itemSearchValues(item), queryMatchState).rank > QUERY_MATCH_RANK_NONE
    ) {
      return true;
    }
  }
  return false;
}

function findFishFilterMatches(searchText, selectedTerms, queryMatchState = searchText) {
  const query = normalizeQueryMatchText(searchText);
  const terms = query ? query.split(/\s+/g).filter(Boolean) : [];
  if (!terms.length) {
    return [];
  }
  const selected = new Set(normalizeFishFilterTerms(selectedTerms));
  const matches = [];
  const metadataByTerm = fishFilterMetadata();
  for (const term of FISH_FILTER_TERM_ORDER) {
    if (selected.has(term)) {
      continue;
    }
    const metadata = metadataByTerm[term];
    const isGradeFilter = FISH_GRADE_FILTER_TERMS.has(term);
    const searchValues = fishFilterSearchValues(term, metadata);
    const match = searchMatchLookup(searchValues, queryMatchState);
    if (match.rank <= QUERY_MATCH_RANK_NONE) {
      continue;
    }
    matches.push({
      kind: FISH_GRADE_FILTER_TERMS.has(term) ? "grade" : "status",
      ...(FISH_GRADE_FILTER_TERMS.has(term) ? { grade: term } : { status: term }),
      term,
      label: metadata?.label || term,
      description: metadata?.description || "",
      _score: match.score + (isGradeFilter ? 25 : 0),
      _matchRank: fishFilterMatchRank(term, searchText, metadata, queryMatchState),
      _queryMatch: match,
    });
  }
  matches.sort((left, right) => {
    if (right._score !== left._score) {
      return right._score - left._score;
    }
    return String(left.label || "").localeCompare(String(right.label || ""));
  });
  return matches;
}

export function buildDefaultFishFilterMatches(stateBundle) {
  const metadataByTerm = fishFilterMetadata();
  const selected = new Set(resolveSelectedFishFilterTerms(stateBundle));
  return FISH_FILTER_TERM_ORDER.filter((term) => !selected.has(term)).map((term) => ({
    kind: FISH_GRADE_FILTER_TERMS.has(term) ? "grade" : "status",
    ...(FISH_GRADE_FILTER_TERMS.has(term) ? { grade: term } : { status: term }),
    term,
    label: metadataByTerm[term]?.label || term,
    description: metadataByTerm[term]?.description || "",
    _score: 0,
  }));
}

function buildDefaultSpecialItemMatches(stateBundle) {
  const selectedKeys = selectedItemTermKeys(stateBundle);
  return specialItemReferencesForCatalogItems(stateBundle?.state?.catalog?.items || [])
    .map((reference) => ({ ...reference, _score: 0 }))
    .filter((match) => !itemMatchIsSelected(match, selectedKeys));
}

function buildPatchBoundPromptMatches(stateBundle) {
  const selectedBounds = resolveSelectedPatchBoundSet(stateBundle);
  return patchBoundPromptMatches()
    .filter((match) => !selectedBounds.has(match.bound))
    .map((match) => ({
      kind: "patch-bound",
      bound: match.bound,
      label: match.label,
      description: match.description,
      _score: 0,
    }));
}

export function buildDefaultSearchMatches(stateBundle) {
  return buildDefaultFishFilterMatches(stateBundle)
    .concat(buildDefaultSpecialItemMatches(stateBundle), buildPatchBoundPromptMatches(stateBundle));
}

function compareSemanticMatchesForIdentity(left, right) {
  if (right._score !== left._score) {
    return right._score - left._score;
  }
  return String(left.label || "").length - String(right.label || "").length;
}

function findSemanticMatches(semanticTerms, searchText, options = {}) {
  const query = normalizeQueryMatchText(searchText);
  if (!query) {
    return [];
  }
  const bounded = Number.parseInt(options.limit, 10) > 0;
  const filteredByKey = bounded ? null : new Map();
  const filtered = [];
  const boundedOneCharacter = bounded &&
    compactQueryMatchText(searchText).length === 1;
  const catalogIndex = SEMANTIC_CATALOG_INDEX(semanticTerms);
  const candidateRecords = boundedOneCharacter
    ? queryCatalogOneCharacterCandidates(
        catalogIndex,
        searchText,
        {
          limit: options.limit,
          prefixOnly: options.prefixOnly === true,
          includeRecord: (record) => options.excludeMatch?.(record.value) !== true,
        },
      )
    : options.prefixOnly === true
      ? queryCatalogPrefixCandidates(catalogIndex, searchText)
      : queryCatalogCandidates(catalogIndex, searchText);
  for (const record of candidateRecords) {
    const term = record.value;
    if (!term || String(term.layerId || "").trim().toLowerCase() === "zone_mask") {
      continue;
    }
    const match = queryCatalogRecordMatch(
      catalogIndex,
      record,
      options.queryMatchState || searchText,
    );
    if (match.rank <= QUERY_MATCH_RANK_NONE) {
      continue;
    }
    const candidate = {
      kind: "semantic",
      ...term,
      _score: match.score,
      _matchRank: match.rank,
      _queryMatch: match,
    };
    const key = semanticTermLookupKey(term.layerId, term.fieldId);
    if (bounded) {
      addSearchMatch(filtered, candidate, {
        ...options,
        dedupeKey: (value) => semanticTermLookupKey(value?.layerId, value?.fieldId),
        compareDuplicate: compareSemanticMatchesForIdentity,
      });
      continue;
    }
    const existing = filteredByKey.get(key);
    if (
      !existing ||
      candidate._score > existing._score ||
      (candidate._score === existing._score &&
        String(candidate.label || "").length < String(existing.label || "").length)
    ) {
      filteredByKey.set(key, candidate);
    }
  }
  if (!bounded) {
    filtered.push(...filteredByKey.values());
  }
  return finishSearchMatches(filtered, options, (left, right) => {
    if (right._score !== left._score) {
      return right._score - left._score;
    }
    if (left.layerName !== right.layerName) {
      return String(left.layerName || "").localeCompare(String(right.layerName || ""));
    }
    return String(left.label || "").localeCompare(String(right.label || ""));
  });
}

function findTradeNpcMatches(tradeNpcs, searchText, options = {}) {
  const query = normalizeQueryMatchText(searchText);
  if (!query) {
    return [];
  }
  const catalogIndex = TRADE_NPC_CATALOG_INDEX(tradeNpcs);
  const boundedOneCharacter = compactQueryMatchText(searchText).length === 1 &&
    Number.parseInt(options.limit, 10) > 0;
  const candidateRecords = boundedOneCharacter
    ? queryCatalogOneCharacterCandidates(catalogIndex, searchText, {
        limit: options.limit,
        prefixOnly: options.prefixOnly === true,
        includeRecord: (record) => options.excludeMatch?.(record.value) !== true,
      })
    : options.prefixOnly === true
      ? queryCatalogPrefixCandidates(catalogIndex, searchText)
      : queryCatalogCandidates(catalogIndex, searchText);
  const matches = [];
  for (const record of candidateRecords) {
    const npc = record.value;
    if (options.excludeMatch?.(npc) === true) {
      continue;
    }
    const match = queryCatalogRecordMatch(
      catalogIndex,
      record,
      options.queryMatchState || searchText,
    );
    if (
      match.rank <= QUERY_MATCH_RANK_NONE ||
      (options.prefixOnly === true && match.rank < QUERY_MATCH_RANK_PREFIX)
    ) {
      continue;
    }
    addSearchMatch(matches, {
      kind: "trade-npc",
      ...npc,
      _score: match.score,
      _matchRank: match.rank,
      _queryMatch: match,
    }, options);
  }
  return finishSearchMatches(matches, options, (left, right) => {
    if (right._score !== left._score) {
      return right._score - left._score;
    }
    return String(left.npcName || "").localeCompare(String(right.npcName || ""));
  });
}

function findPatchPromptMatches(searchText, stateBundle, queryMatchState = searchText) {
  const query = normalizeQueryMatchText(searchText);
  const queryTerms = query ? query.split(/\s+/g).filter(Boolean) : [];
  if (!queryTerms.length) {
    return [];
  }
  const selectedBounds = resolveSelectedPatchBoundSet(stateBundle);
  const matches = [];
  for (const candidate of patchBoundPromptMatches()) {
    if (selectedBounds.has(candidate.bound)) {
      continue;
    }
    const match = searchMatchLookup([
      candidate.bound,
      patchBoundQueryPrefix(candidate.bound),
      candidate.label,
      candidate.searchText,
    ], queryMatchState);
    if (match.rank <= QUERY_MATCH_RANK_NONE) {
      continue;
    }
    matches.push({
      kind: "patch-bound",
      bound: candidate.bound,
      label: candidate.label,
      description: candidate.description,
      _score: match.score,
      _matchRank: match.rank,
      _queryMatch: match,
    });
  }
  matches.sort((left, right) => {
    if (right._score !== left._score) {
      return right._score - left._score;
    }
    return String(left.label || "").localeCompare(String(right.label || ""));
  });
  return matches;
}

function searchMatchPriority(match) {
  if (match?.kind === "status" || match?.kind === "grade") {
    return 0;
  }
  if (match?.kind === "fish") {
    return 1;
  }
  if (match?.kind === "item" || match?.kind === "item-group") {
    return 1;
  }
  if (match?.kind === "zone") {
    return 2;
  }
  if (match?.kind === "trade-npc") {
    return 2;
  }
  if (match?.kind === "semantic") {
    return 3;
  }
  if (match?.kind === "patch-bound") {
    return 4;
  }
  return 9;
}

function buildSearchMatchesWithOptions(stateBundle, searchText, zoneCatalog = [], options = {}) {
  const catalogFish = stateBundle?.state?.catalog?.fish || [];
  const catalogItems = stateBundle?.state?.catalog?.items || [];
  const semanticTerms = stateBundle?.state?.catalog?.semanticTerms || [];
  const tradeNpcs = stateBundle?.state?.catalog?.tradeNpcs || [];
  const selectedFishIds = new Set(resolveSelectedFishIds(stateBundle));
  const selectedItemIds = new Set(resolveSelectedItemIds(stateBundle));
  const selectedFishFilterTerms = resolveSelectedFishFilterTerms(stateBundle);
  const selectedSemanticFieldIdsByLayer = resolveSelectedSemanticFieldIdsByLayer(stateBundle);
  const selectedZoneRgbs = new Set(resolveSelectedZoneRgbs(stateBundle));
  const selectedTradeNpcKeys = resolveSelectedTradeNpcKeys(stateBundle);
  const sharedFishState = normalizeSharedFishState(stateBundle?.sharedFishState);
  const filterDirectives = parseFishFilterDirectives(searchText);
  const queryMatchState = prepareQueryMatchState(filterDirectives.rawQuery);
  const remainingQueryMatchState = filterDirectives.remainingQuery === filterDirectives.rawQuery
    ? queryMatchState
    : prepareQueryMatchState(filterDirectives.remainingQuery);
  const effectiveFishFilterTerms = normalizeFishFilterTerms(filterDirectives.directTerms);
  const fishFilterMatches = findFishFilterMatches(
    searchText,
    selectedFishFilterTerms,
    queryMatchState,
  );
  const patchMatches = findPatchPromptMatches(
    filterDirectives.remainingQuery,
    stateBundle,
    remainingQueryMatchState,
  );
  const selectedItemKeys = selectedItemTermKeys(stateBundle);
  const itemIdentityIds = new Set();
  for (const itemId of selectedItemIds) {
    itemIdentityIds.add(itemId);
  }
  const matchCollectionOptions = {
    limit: options.limit,
    prefixOnly: options.prefixOnly,
    dedupeKey: options.dedupeKey,
    queryMatchState: remainingQueryMatchState,
  };
  const itemMatches = findItemMatches(catalogItems, filterDirectives.remainingQuery, {
    ...matchCollectionOptions,
    matchedIdentityIds: itemIdentityIds,
    hasExclusions: selectedItemKeys.size > 0,
    excludeMatch: (item) => itemMatchIsSelected(item, selectedItemKeys),
  });
  const fishMatches = findFishMatches(catalogFish, filterDirectives.remainingQuery, {
    ...matchCollectionOptions,
    includeAllWhenEmpty: effectiveFishFilterTerms.length > 0,
    filterTerms: effectiveFishFilterTerms,
    sharedFishState,
    excludeMatch: (fish) =>
      selectedFishIds.has(fish.fishId) ||
      fishMatchIsCoveredByItems(fish, itemIdentityIds) ||
      (options.prefixOnly === true && fishMatchIsCoveredByCatalogItems(
        fish,
        catalogItems,
        remainingQueryMatchState,
      )),
  });
  const zoneCandidates = indexedZoneCatalogEntries(
    zoneCatalog,
    filterDirectives.remainingQuery,
    {
      prefixOnly: options.prefixOnly,
      limit: options.limit,
      includeEntry: (zone) => !selectedZoneRgbs.has(zone.zoneRgb),
    },
  );
  const zoneMatches = [];
  for (const zone of findZoneMatches(zoneCandidates, filterDirectives.remainingQuery)) {
    addSearchMatch(zoneMatches, {
      ...zone,
      _matchRank: zone?._matchRank ?? searchMatchLookupRank([
        zone?.zoneRgb,
        zone?.name,
        zone?.rgbKey,
        zone?.rgbSpaced,
        zone?.normalizedKey,
        zone?.normalizedSpaced,
        zone?.hexKey,
        zone?.hashHexKey,
        zone?.bareHexKey,
      ], remainingQueryMatchState),
    }, {
      ...matchCollectionOptions,
      excludeMatch: (candidate) => selectedZoneRgbs.has(candidate.zoneRgb),
    });
  }
  const semanticMatches = findSemanticMatches(semanticTerms, filterDirectives.remainingQuery, {
    ...matchCollectionOptions,
    excludeMatch: (term) =>
      (selectedSemanticFieldIdsByLayer[term.layerId] || []).includes(term.fieldId),
  });
  const tradeNpcMatches = findTradeNpcMatches(tradeNpcs, filterDirectives.remainingQuery, {
    ...matchCollectionOptions,
    excludeMatch: (npc) => selectedTradeNpcKeys.has(Number.parseInt(npc?.npcKey, 10)),
  });
  const globalCollectionOptions = {
    limit: options.limit,
    dedupeKey: options.dedupeKey,
  };
  const matches = [];
  for (const match of fishFilterMatches.concat(
    patchMatches,
    fishMatches,
    itemMatches,
    tradeNpcMatches,
    semanticMatches,
    zoneMatches,
  )) {
    if (
      options.prefixOnly === true &&
      searchMatchRank(match) < QUERY_MATCH_RANK_PREFIX &&
      match?._matchMode !== "rgb-exact"
    ) {
      continue;
    }
    addSearchMatch(matches, match, globalCollectionOptions);
  }
  return finishSearchMatches(matches, globalCollectionOptions, compareSearchMatches);
}

export function buildSearchMatches(stateBundle, searchText, zoneCatalog = []) {
  return buildSearchMatchesWithOptions(stateBundle, searchText, zoneCatalog);
}

export function patchTouchesSearchPanelSignals(patch) {
  if (!isPlainObject(patch)) {
    return false;
  }
  if (patch._map_ui?.search != null) {
    return true;
  }
  if (patch._map_bridged?.filters != null) {
    return true;
  }
  if (patch._map_runtime?.ready != null) {
    return true;
  }
  if (patch._map_runtime?.catalog != null) {
    return true;
  }
  if (patch._map_runtime?.catalogRevision != null) {
    return true;
  }
  if (patch._shared_fish != null) {
    return true;
  }
  return false;
}

export function fishFilterTermMetadata(term) {
  return fishFilterMetadata()[normalizeFishFilterTerm(term)] || null;
}
